/**
 * PDF生成ロジック
 * ReportAndDraft.tsx から抽出した純粋async関数
 */

// PDF関連
import { toJpeg } from 'html-to-image';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { RowInput } from 'jspdf-autotable';
import headerLogo from '../../../assets/logo.png';
import NotoSansJPRegular from '../../../utils/NotoSansJP-Regular';

// 日付操作
import dayjs from 'dayjs';
// 浮動小数点型
import Decimal from 'decimal.js';

// Chart.js（canvasリサイズ用）
import { Chart as ChartJS } from 'chart.js';

// ReactFlow
import { ReactFlowInstance } from 'reactflow';

// 型定義
import type {
  ApproverList,
  Currency,
  EquipmentSupplierAndEqIdType,
  EquipmentType,
  FacilityItem,
  MoldTable,
  OriginalProfitAndLossTable,
  PartNo,
  ProductionLineType,
  QuantityAndPriceTable,
  ReviewScheduleItem,
  ScheduleItem,
  UpdateInvestmentProfitLossData,
  UpdateSummaryInputData,
  RationalizationInvestmentProfitLossData,
} from '../../../contexts/types';

// 定数
import {
  PricingAgreementCategory,
  PurposeType,
  EquipmentUsageType,
  ReviewPhasePresets,
  getPlannedBudgetTypeNameByCode,
  isScanBlocked,
} from '../../../contexts/constants';

import {
  getEquipmentCategoryNamesByCodes,
  getPurposeTypeNameByCode,
} from '../../../contexts/constants';

// ユーティリティ
import {
  formatNumber,
  getLocalCurrencyId,
  parseCompanyPlantSnapshot,
} from '../../../utils/utils';
import i18n from '../../../utils/i18n';
import { getMultilingualDisplayName, getBilingualDisplayName, getDivisionHierarchyDisplay } from '../../../utils/i18nDisplay';
import { splitEscapedList, resolveDisplayPartNoByKey } from '../../../utils/utils';

// PDF専用ユーティリティ
import {
  convertHtmlToImageJpeg,
  convertHtmlToImageJpegUsingHtml2Canvas,
  convertHtmlToImagePng,
  scaleImage,
  calculateScaleToFitPage,
} from './pdfUtils';

// PDF定数
import {
  QUANTITY_PRICE_PARTNO_TABLE_PREFIX,
  QUANTITY_PRICE_MOLD_TABLE_PREFIX,
  QUANTITY_PRICE_DEV_TABLE_PREFIX,
  QUANTITY_PRICE_EQUIPMENT_TABLE_PREFIX,
  QUANTITY_PRICE_PROJECT_TOTAL_TABLE_PREFIX,
  PROFIT_AND_LOSS_PARTNO_TABLE_PREFIX,
  PROFIT_AND_LOSS_PARTNO_OUTSOURC_TABLE_PREFIX,
  PROFIT_AND_LOSS_PARTNO_INHOUSE_TABLE_PREFIX,
  PROFIT_AND_LOSS_MOLD_TABLE_PREFIX,
  PROFIT_AND_LOSS_DEV_TABLE_PREFIX,
  PROFIT_AND_LOSS_EQUIPMENT_TABLE_PREFIX,
  PROFIT_AND_LOSS_GRAND_TOTAL_TABLE_PREFIX,
  PROFIT_AND_LOSS_DCF_TABLE_PREFIX,
  PROFIT_AND_LOSS_PAYBACK_PERIOD_TABLE_PREFIX,
  PROFIT_AND_LOSS_COLLECT_SIMULATION_TABLE_PREFIX,
  UPDATE_IMPROVEMENT_EFFECT_PROCESS_ITEM_PREFIX,
  UPDATE_IMPROVEMENT_INDICATOR_PROCESS_PREFIX,
  UPDATE_IMPROVEMENT_COST_PROCESS_PREFIX,
  UPDATE_IMPROVEMENT_EFFECT_TOTAL_ID,
  RATIONALIZATION_BEFORE_PROFIT_LOSS_ID,
  RATIONALIZATION_AFTER_PROFIT_LOSS_ID,
  RATIONALIZATION_IMPROVEMENT_INDICATOR_PROCESS_PREFIX,
  RATIONALIZATION_IMPROVEMENT_COST_PROCESS_PREFIX,
  RATIONALIZATION_IMPROVEMENT_EFFECT_TOTAL_ID,
  UPDATE_SUMMARY_ITEM_PREFIX,
  PRODUCTION_CAPACITY_PROCESS_ITEM_PREFIX,
  RATIONALIZATION_SUMMARY_CHART_ITEM_PREFIX,
  RATIONALIZATION_SUMMARY_CHARTS_PER_PAGE,
  CONFIRMER_LIST_BOX_PREFIX,
  APPROVAL_LIST_BOX_PREFIX,
} from './pdfConstants';

// ---- 型エイリアス ----
type jsPDFCustom = jsPDF;

// ---- フォント事前登録キャッシュ ----
// addFileToVFS + addFont の処理結果（vFS）を初回1回だけ実行してキャッシュする。
// 2回目以降は vFS をコピーするだけなので addFont のTTFパースコストがかからない。
// jsPDF の型定義に vFS が含まれないため、internal へのアクセスは any キャストを使用する。
type FontCache = { vFS: Record<string, string> };
type jsPDFWithVFS = jsPDF & { internal: { vFS: Record<string, string> } };
let _fontCachePromise: Promise<FontCache> | null = null;

const ensureFontRegistered = (pdf: jsPDFCustom): Promise<void> => {
  // キャッシュ済みなら vFS をコピーして addFont のみ実行
  if (_fontCachePromise) {
    return _fontCachePromise.then(cache => {
      (pdf as unknown as jsPDFWithVFS).internal.vFS = { ...cache.vFS };
      pdf.addFont('NotoSansJP-Regular.ttf', 'NotoSansJP', 'normal');
      pdf.addFont('NotoSansJP-Regular.ttf', 'NotoSansJP', 'bold');
    });
  }
  // 初回: Promise をキャッシュしてから実行
  _fontCachePromise = new Promise<FontCache>(resolve => {
    setTimeout(() => {
      const tempPdf = new jsPDF('landscape', 'mm', 'a4');
      tempPdf.addFileToVFS('NotoSansJP-Regular.ttf', NotoSansJPRegular);
      tempPdf.addFont('NotoSansJP-Regular.ttf', 'NotoSansJP', 'normal');
      tempPdf.addFont('NotoSansJP-Regular.ttf', 'NotoSansJP', 'bold');
      resolve({ vFS: { ...(tempPdf as unknown as jsPDFWithVFS).internal.vFS } });
    }, 0);
  });
  return _fontCachePromise.then(cache => {
    (pdf as unknown as jsPDFWithVFS).internal.vFS = { ...cache.vFS };
    pdf.addFont('NotoSansJP-Regular.ttf', 'NotoSansJP', 'normal');
    pdf.addFont('NotoSansJP-Regular.ttf', 'NotoSansJP', 'bold');
  });
};

/**
 * フォントをバックグラウンドで事前登録する。
 * ReportAndDraft.tsx のマウント時に呼び出すことで、
 * PDF出力ボタン押下時にはフォント登録済み状態にしておく。
 */
export const prewarmPdfFont = (): void => {
  if (_fontCachePromise) return;
  _fontCachePromise = new Promise<FontCache>(resolve => {
    setTimeout(() => {
      const tempPdf = new jsPDF('landscape', 'mm', 'a4');
      tempPdf.addFileToVFS('NotoSansJP-Regular.ttf', NotoSansJPRegular);
      tempPdf.addFont('NotoSansJP-Regular.ttf', 'NotoSansJP', 'normal');
      tempPdf.addFont('NotoSansJP-Regular.ttf', 'NotoSansJP', 'bold');
      resolve({ vFS: { ...(tempPdf as unknown as jsPDFWithVFS).internal.vFS } });
    }, 0);
  });
};

// ---- PDF固有の型定義 ----

export interface TotalYearlyData {
  totalSales: { [year: string]: Decimal };
  totalMaterialCost: { [year: string]: Decimal };
  totalThroughput: { [year: string]: Decimal };
  appliedHeadOfficeSalesManagementRatio: { [year: string]: Decimal };
  ebitda: { [year: string]: Decimal };
  capexRow: { [year: string]: Decimal };
  cumulativeEbitdaAndCapex: { [year: string]: Decimal };
  depreciation: { [year: string]: Decimal };
  depreciationDcf: { [year: string]: Decimal };
  ebit: { [year: string]: Decimal };
  nopat: { [year: string]: Decimal };
  undepreciated: { [year: string]: Decimal };
  roic: { [year: string]: Decimal };
  tax: { [year: string]: Decimal };
  fcf: { [year: string]: Decimal };
  pv: { [year: string]: Decimal };
  dcf: { [year: string]: Decimal };
}

/** 確認済み者情報（確認者セクション表示用） */
export interface ConfirmedPerson {
  divisionId: string;
  confirmKbn: string;
  employeeName: string;
  latestConfirmTime: string | null;
}

/** アップロード済みファイル */
export interface UploadedFile {
  key: string;
  fileName: string;
  lastModified: string;
  size: number;
  file: File;
  url: string | null;
}

/** フォルダ別アップロード済みファイル */
export interface UploadedFilesByFolder {
  folderName: string;
  files: UploadedFile[];
}

// ---- コンテキスト型定義 ----

/** プロジェクト関連データ */
export interface PdfProjectContext {
  data: any; // ProjectWithRdsData | undefined
  currencyData: Currency[];
  selectedCurrency: { id: string; name: string };
  rateType: string;
  ratePeriod: string;
  customerNationalityName: string;
  uploadedImageFileContent: string | null;
  productionLineData: ProductionLineType;
  monthList: string[];
  scheduleItems: ScheduleItem[];
  facilityItems: FacilityItem[];
  reviewItems: ReviewScheduleItem[];
  /** 簡易モードかどうか */
  isSimpleMode: boolean;
  /** 損益非表示モードかどうか（簡易様式かつ「損益を表示しない」選択時。P&L/Simulationの表・グラフを出力しない） */
  isProfitLossHiddenMode: boolean;
}

/** 販売関連データ */
export interface PdfSalesContext {
  partNoData: PartNo[];
  partNoDataMap: Map<string, any> | null;
  quantityAndPriceTables: QuantityAndPriceTable[];
  convQuantityAndPriceTables: QuantityAndPriceTable[];
  moldTables: MoldTable[];
  developmentCostTables: any[];
  equipmentCostTables: any[];
  pricingAgreementSentence: any;
}

/** 設備関連データ */
export interface PdfEquipmentContext {
  amounts: EquipmentType[];
  convAmounts: EquipmentType[];
  supplierData: EquipmentSupplierAndEqIdType[];
  processItemNameMap: { [processId: string]: string };
  processItemNoMap: { [processId: string]: string };
  processSeqMap: { [processId: string]: number };
}

/** 損益関連データ */
export interface PdfProfitAndLossContext {
  display: OriginalProfitAndLossTable;
  totalYearlyData: TotalYearlyData;
  maxYears: number[];
  moldSalesByProjectYear: Decimal[];
  devSalesByProjectYear: Decimal[];
  equipmentSalesByProjectYear: Decimal[];
  updateData: UpdateInvestmentProfitLossData | null;
  rationalizationData: RationalizationInvestmentProfitLossData | null;
  updateSummaryData: UpdateSummaryInputData[];
}

/** シミュレーション関連データ */
export interface PdfSimulationContext {
  productionCapacityProcessChartDataList: {
    processId: string;
    processName: string;
    beforeData: any;
    beforeDownsideData: any;
    afterData: any;
    afterDownsideData: any;
  }[];
  rationalizationSummaryChartDataList: {
    processId: string;
    itemNo: string;
    labels: string[];
    items: {
      itemId: string;
      itemName: string;
      unit: string;
      beforeData: number[];
      afterData: number[];
      group: "cost" | "indicator" | "improvementEffect";
      depreciationData?: number[];
    }[];
  }[];
}

/** 承認関連データ */
export interface PdfApprovalContext {
  approverList: ApproverList[];
  executiveManager: ApproverList[];
  confirmedPersonsList: ConfirmedPerson[];
}

/** チェックリスト関連データ */
export interface PdfCheckListContext {
  estimateData: {
    [equipmentId: string]: { estimateCheckList: any };
  };
  dataList: {
    [equipmentId: string]: { checkList: any };
  };
}

/** PDF生成コンテキスト（全データのルート） */
export interface PdfGenerationContext {
  project: PdfProjectContext;
  sales: PdfSalesContext;
  equipment: PdfEquipmentContext;
  profitAndLoss: PdfProfitAndLossContext;
  simulation: PdfSimulationContext;
  approval: PdfApprovalContext;
  checkList: PdfCheckListContext;
}

/** DOM ref 参照 */
export interface PdfRefs {
  pdfRef: React.RefObject<HTMLDivElement>;
  scheduleRef: React.RefObject<HTMLDivElement>;
  equipmentFlowRef: React.RefObject<any>;
  reactFlowRef: React.RefObject<ReactFlowInstance | null>;
  benchmarkRefs: React.RefObject<Array<HTMLDivElement | null>>;
  estimateCheckListRefs: React.RefObject<(HTMLDivElement | null)[]>;
  checkListRefs: React.RefObject<(HTMLDivElement | null)[]>;
  simulationRef: React.RefObject<any>;
  simulationRef2: React.RefObject<any>;
  simulationRef3: React.RefObject<any>;
}

/** 外部サービス */
export interface PdfServices {
  uploadedFilesByFolder: UploadedFilesByFolder[];
  fetchAllBase64FromS3: (files: UploadedFile[] | undefined) => Promise<string[]>;
  toast: (options: { title: string; status: 'info' | 'error' | 'loading' | 'warning' | 'success'; duration: number; isClosable: boolean }) => void;
}

/**
 * generatePDF の任意フック群。
 * 呼び出し元がPDFの特定タイミングで追加処理を差し込めるようにする（振り返り3画面の挿入など）。
 * 振り返り処理は呼び出し元（ReportAndDraft.tsx）に閉じ込め、generatePDF本体は内容を知らない設計とする。
 */
export interface PdfHooks {
  /** 「見積チェックリスト」セクションを描画する直前に呼ばれる。pdf に addPage / addImage 等で追記してよい */
  beforeEstimateCheckList?: (pdf: jsPDF) => Promise<void>;
}

// ---- ユーティリティ関数（generatePDF内で使用） ----

// ---- スケジュールPDF描画用ユーティリティは scheduleUtils.ts に集約 ----

/** 通貨名フィルター */
const currencyNameFilter = (currencyData: any, targetData: any): string => {
  if (targetData && currencyData) {
    const currencyId = targetData.companyPlantSnapshot
      ? getLocalCurrencyId(targetData.companyPlantSnapshot)
      : targetData.id;
    const found = currencyData?.find((c: any) => c.currencyId === currencyId);
    return found?.currencyName ?? "--";
  }
  return "--";
};

/** 通貨レートフィルター */
const currencyRateFilter = (currencyData: any, targetData: any): number | null => {
  if (targetData && currencyData) {
    const currencyId = targetData.companyPlantSnapshot
      ? getLocalCurrencyId(targetData.companyPlantSnapshot)
      : targetData.id;
    const found = currencyData?.find((c: any) => c.currencyId === currencyId);
    return found?.exchangeRate ?? null;
  }
  return null;
};

/** 通貨レート変換 */
const formatExchangeRate = (rate: number | undefined): string => {
  if (rate === undefined || rate === null) return '--';
  const str = String(rate);
  const decimalIndex = str.indexOf('.');
  if (decimalIndex === -1) {
    return rate.toFixed(2);
  }
  const decimalPlaces = str.length - decimalIndex - 1;
  if (decimalPlaces < 2) {
    return rate.toFixed(2);
  }
  return str;
};

/** 設備の合計金額(投資額)を算出する */
const calculateTotalAmount = (equipmentAmounts: { amount: string }[]): Decimal => {
  const totalAmount = equipmentAmounts.reduce((total: Decimal, equipment: any) => {
    const amount = new Decimal(equipment.amount ?? 0);
    return total.plus(amount);
  }, new Decimal(0));
  return totalAmount;
};

/** EOL合計数量を算出する */
const calculateTotalQuantity = (table: QuantityAndPriceTable): Decimal => {
  return table.years.reduce((total, year) => {
    const quantity = table.data[year]?.quantity || 0;
    return total.plus(new Decimal(quantity));
  }, new Decimal(0));
};

/** 単純工合計人数 */
const totalNumberOfSimpleLabor = (equipmentAmounts: { unskilledWorker: number }[]): number => {
  return equipmentAmounts.reduce((total: number, equipment: { unskilledWorker: number }) => {
    return total + (equipment.unskilledWorker ?? 0);
  }, 0);
};

/** 多能工合計人数 */
const totalNumberOfMultiSkilledWorkerse = (equipmentAmounts: { multiSkilledWorker: number }[]): number => {
  return equipmentAmounts.reduce((total: number, equipment: { multiSkilledWorker: number }) => {
    return total + (equipment.multiSkilledWorker ?? 0);
  }, 0);
};

/** テクニシャン合計人数 */
const totalNumberOfTechnicians = (equipmentAmounts: { technician: number }[]): number => {
  return equipmentAmounts.reduce((total: number, equipment: { technician: number }) => {
    return total + (equipment.technician ?? 0);
  }, 0);
};

/** サプライヤー名の重複削除 */
const removeDuplicateSupplierNames = (suppliers: EquipmentSupplierAndEqIdType[]): string[] => {
  const uniqueNamesSet = new Set<string>();
  suppliers.forEach(supplier => {
    if (supplier.equipmentSupplierName) {
      uniqueNamesSet.add(getMultilingualDisplayName(supplier.equipmentSupplierName));
    }
  });
  return Array.from(uniqueNamesSet);
};

/**
 * 申請金額を返す
 * @param validEquipmentAmounts 設備投資金額
 * @param currencyName 通貨名
 * @param applicationMonth 申請月度
 * @returns 申請金額（設備投資額と設備投資総額）
 */
const getRequestedAmount = (
  validEquipmentAmounts: { amount: string; applicationPlannedMonth?: string }[],
  currencyName: string,
  applicationMonth: string | null | undefined
): string => {
  // 申請月度と申請予定月が一致する設備をフィルタリング
  const matchingEquipments = validEquipmentAmounts.filter(
    (item) => item.applicationPlannedMonth && item.applicationPlannedMonth === applicationMonth
  );
  // 設備投資額（申請月度と一致する設備の合計）
  const investmentAmount = calculateTotalAmount(matchingEquipments as { amount: string }[]);
  // 設備投資総額（全設備の合計）
  const totalAmount = calculateTotalAmount(validEquipmentAmounts as { amount: string }[]);

  return `${formatNumber(investmentAmount, "WithDecimal")} ${i18n.t('common.label.thousandSuffix')}${currencyName}（${i18n.t('features.InvestmentScreening.pdf.label.totalInvestmentAmount')}：${formatNumber(totalAmount, "WithDecimal")} ${i18n.t('common.label.thousandSuffix')}${currencyName}）`;
};


// ---- メイン関数 ----

/**
 * PDF生成のメイン関数
 * @param ctx PDF生成に必要なデータコンテキスト
 * @param refs DOM参照
 * @param services 外部サービス（ファイル取得、トースト通知等）
 * @param hooks 任意の差し込みフック（振り返り3画面など、特定タイミングで追加処理を差し込む）
 * @returns 組み立て済みjsPDFインスタンス。saveは呼び出し元で実行する。失敗時はnull
 */
export async function generatePDF(
  ctx: PdfGenerationContext,
  refs: PdfRefs,
  services: PdfServices,
  hooks?: PdfHooks
): Promise<jsPDF | null> {

  // コンテキストから変数を展開
  const projectData = ctx.project.data;
  const currencyData = ctx.project.currencyData;
  const selectedCurrency = ctx.project.selectedCurrency;
  const rateType = ctx.project.rateType;
  const ratePeriod = ctx.project.ratePeriod;
  const customerNationalityName = ctx.project.customerNationalityName;
  const uploadedImageFileContent = ctx.project.uploadedImageFileContent;
  const productionLineData = ctx.project.productionLineData;
  const isSimpleMode = ctx.project.isSimpleMode;
  const isProfitLossHiddenMode = ctx.project.isProfitLossHiddenMode;

  const partNoData = ctx.sales.partNoData;
  const partNoDataMap = ctx.sales.partNoDataMap;
  const quantityAndPriceTables = ctx.sales.quantityAndPriceTables;
  const pricingAgreementSentence = ctx.sales.pricingAgreementSentence;

  const equipmentAmounts = ctx.equipment.amounts;
  const convEquipmentAmounts = ctx.equipment.convAmounts;
  const supplierData = ctx.equipment.supplierData;
  const processItemNameMap = ctx.equipment.processItemNameMap;
  const processItemNoMap = ctx.equipment.processItemNoMap;
  const processSeqMap = ctx.equipment.processSeqMap;

  const dispProfitAndLoss = ctx.profitAndLoss.display;
  const totalYearlyData = ctx.profitAndLoss.totalYearlyData;
  const maxYears = ctx.profitAndLoss.maxYears;
  const moldSalesByProjectYear = ctx.profitAndLoss.moldSalesByProjectYear;
  const devSalesByProjectYear = ctx.profitAndLoss.devSalesByProjectYear;
  const equipmentSalesByProjectYear = ctx.profitAndLoss.equipmentSalesByProjectYear;
  const updateInvestmentProfitLossData = ctx.profitAndLoss.updateData;
  const rationalizationInvestmentProfitLossData = ctx.profitAndLoss.rationalizationData;
  const updateSummaryData = ctx.profitAndLoss.updateSummaryData;

  const productionCapacityProcessChartDataList = ctx.simulation.productionCapacityProcessChartDataList;
  const rationalizationSummaryChartDataList = ctx.simulation.rationalizationSummaryChartDataList;

  const executiveManager = ctx.approval.executiveManager;

  const uploadedFilesByFolder = services.uploadedFilesByFolder;
  const fetchAllBase64FromS3 = services.fetchAllBase64FromS3;
  const toast = services.toast;

  // 汎用設備かどうか
  const isGeneralPurpose = projectData?.equipmentUsageType === EquipmentUsageType.GeneralPurpose.code;
  const labelQuantityAndPrice = isGeneralPurpose
    ? i18n.t('features.InvestmentScreening.pdf.label.quantityAndPriceSimple')
    : i18n.t('features.InvestmentScreening.pdf.label.quantityAndPrice');
  // サプライヤー名の重複削除
  const uniqueSupplierNames = removeDuplicateSupplierNames(supplierData).join('、');

  // React再レンダリングをバイパスしてDOM直接操作でローディング即時表示
  // （WebAPI.setLoading(true) は42コンポーネントの再レンダリングを引き起こし~618msブロックするため使用しない）
  // Chakra UI の CircularProgress と同じ見た目を再現
  if (!document.getElementById('pdf-loading-style')) {
    const style = document.createElement('style');
    style.id = 'pdf-loading-style';
    style.textContent = '@keyframes pdf-spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }
  const loadingOverlay = document.createElement('div');
  loadingOverlay.id = 'pdf-loading-overlay';
  loadingOverlay.setAttribute('data-html2canvas-ignore', 'true'); // html2canvasのドキュメントクローンから除外
  loadingOverlay.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:9999;display:flex;align-items:center;justify-content:center;text-align:center;background:rgba(0,0,0,0.16);backdrop-filter:blur(10px) hue-rotate(90deg);';
  loadingOverlay.innerHTML = '<svg width="80" height="80" viewBox="0 0 100 100" style="animation:pdf-spin 1.5s linear infinite"><circle cx="50" cy="50" r="42" fill="transparent" stroke="#E2E8F0" stroke-width="10"/><circle cx="50" cy="50" r="42" fill="transparent" stroke="#63B3ED" stroke-width="10" stroke-dasharray="264" stroke-dashoffset="198" stroke-linecap="round"/></svg>';
  document.body.appendChild(loadingOverlay);
  // ブラウザが実際にオーバーレイを描画（ペイント）するまで待つ
  // rAFコールバック後にブラウザがペイントし、その後のsetTimeoutで制御が戻る
  await new Promise<void>(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));

  try {
    if (!refs.pdfRef.current) {
      throw new Error("One of the required refs is null");
    }

    // S3画像のプリフェッチ（ネットワーク待ちをPDF組み立てと並行して実行）
    const productionProcessFiles = uploadedFilesByFolder.find(f => f.folderName === "ProductionProcess")?.files;
    const equipmentImagesFiles = uploadedFilesByFolder.find(f => f.folderName === "EquipmentImages")?.files;
    const [prefetchedProductionProcessImages, prefetchedEquipmentImages] = await Promise.all([
      fetchAllBase64FromS3(productionProcessFiles),
      fetchAllBase64FromS3(equipmentImagesFiles),
    ]);

    if (refs.reactFlowRef.current) {
      // ReactFlow図のfitViewを呼び出す
      await refs.reactFlowRef.current.fitView();
    }

    // ===== 画像事前キャプチャフェーズ（並列処理で高速化） =====
    // テーブル要素のスタイルをキャプチャ用に設定するヘルパー
    const prepareElement = (el: HTMLElement | null) => {
      if (el) {
        el.style.overflowX = 'visible';
        el.style.maxWidth = 'none';
      }
      return el;
    };

    // キャプチャタスクを収集（factory パターン: 実際のキャプチャはローディング表示後に遅延実行）
    const captureJobs: { key: string; factory: () => Promise<string | null> }[] = [];

    // キャプチャ前の幅を保存する配列（テーブル系・ページフィット系共通）
    const fitCaptureOriginalStyles: { el: HTMLElement; originalWidth: string }[] = [];

    // テーブル系キャプチャ（JPEG, ratio: 1.5）をスケジュール
    // ピクセル比1.5: CPU処理量44%減で高速化（テーブルはテキスト主体のため画質への影響は軽微）
    const DEFAULT_CAPTURE_WIDTH = 1466;
    const scheduleTableCapture = (elementId: string) => {
      const el = prepareElement(document.getElementById(elementId));
      if (el) {
        // テーブルの実際の幅がデフォルト幅より大きい場合（10年分等）は実際の幅を使用
        const captureWidth = Math.max(DEFAULT_CAPTURE_WIDTH, el.scrollWidth);
        fitCaptureOriginalStyles.push({ el, originalWidth: el.style.width });
        el.style.width = `${captureWidth}px`;
        captureJobs.push({ key: elementId, factory: () => convertHtmlToImageJpeg(el, 1.5, captureWidth) });
      }
    };

    // ページフィット系キャプチャ（JPEG, ratio: 1.5）をスケジュール
    // ブラウザ幅に依存しないよう、要素幅を事前に設定（実際の幅が大きい場合はそちらを使用）
    const scheduleFitCapture = (elementId: string) => {
      const el = document.getElementById(elementId);
      if (el) {
        const captureWidth = Math.max(DEFAULT_CAPTURE_WIDTH, el.scrollWidth);
        fitCaptureOriginalStyles.push({ el, originalWidth: el.style.width });
        el.style.width = `${captureWidth}px`;
        captureJobs.push({ key: elementId, factory: () => convertHtmlToImageJpeg(el, 1.5, captureWidth) });
      }
    };

    // 数量・販売単価 PartNoテーブル
    quantityAndPriceTables.forEach((_, idx) => {
      scheduleTableCapture(`${QUANTITY_PRICE_PARTNO_TABLE_PREFIX}${idx}`);
    });

    // 数量・販売単価 金型/開発費/設備費/プロジェクト合計
    scheduleTableCapture(QUANTITY_PRICE_MOLD_TABLE_PREFIX);
    scheduleTableCapture(QUANTITY_PRICE_DEV_TABLE_PREFIX);
    scheduleTableCapture(QUANTITY_PRICE_EQUIPMENT_TABLE_PREFIX);
    scheduleTableCapture(QUANTITY_PRICE_PROJECT_TOTAL_TABLE_PREFIX);

    // P&L / Simulation 関連のキャプチャ（損益非表示モード時は画面側にDOMが存在しないためスキップ）
    if (!isProfitLossHiddenMode) {
      // P&L PartNoテーブル（本体 + 外注 + 内製）
      if (partNoDataMap) {
        const plEntries = Array.from(partNoDataMap.entries());
        plEntries.forEach((_, idx) => {
          scheduleTableCapture(`${PROFIT_AND_LOSS_PARTNO_TABLE_PREFIX}${idx}`);
          scheduleTableCapture(`${PROFIT_AND_LOSS_PARTNO_OUTSOURC_TABLE_PREFIX}${idx}`);
          scheduleTableCapture(`${PROFIT_AND_LOSS_PARTNO_INHOUSE_TABLE_PREFIX}${idx}`);
        });
      }

      // P&L 金型/開発費/設備費
      scheduleTableCapture(PROFIT_AND_LOSS_MOLD_TABLE_PREFIX);
      scheduleTableCapture(PROFIT_AND_LOSS_DEV_TABLE_PREFIX);
      scheduleTableCapture(PROFIT_AND_LOSS_EQUIPMENT_TABLE_PREFIX);

      // P&L 年度別総計/回収期間/増分CF/DCF
      scheduleFitCapture(PROFIT_AND_LOSS_GRAND_TOTAL_TABLE_PREFIX);
      scheduleTableCapture(PROFIT_AND_LOSS_PAYBACK_PERIOD_TABLE_PREFIX);
      scheduleTableCapture(PROFIT_AND_LOSS_COLLECT_SIMULATION_TABLE_PREFIX);
      scheduleTableCapture(PROFIT_AND_LOSS_DCF_TABLE_PREFIX);

      // 更新投資: 改善効果（プロセスごと + 合計）
      if (projectData?.purpose === PurposeType.UpdateAndOther.code && updateInvestmentProfitLossData) {
        const processCount = updateInvestmentProfitLossData.improvementEffectByProcess?.length || 0;
        for (let i = 0; i < processCount; i++) {
          scheduleFitCapture(`${UPDATE_IMPROVEMENT_EFFECT_PROCESS_ITEM_PREFIX}${i}`);
        }
        scheduleFitCapture(UPDATE_IMPROVEMENT_EFFECT_TOTAL_ID);
      }

      // 合理化投資: 年度別損益（前/後）、改善効果
      if (projectData?.purpose === PurposeType.Rationalization.code && rationalizationInvestmentProfitLossData) {
        scheduleFitCapture(RATIONALIZATION_BEFORE_PROFIT_LOSS_ID);
        scheduleFitCapture(RATIONALIZATION_AFTER_PROFIT_LOSS_ID);
        const indicatorProcessCount = rationalizationInvestmentProfitLossData.improvementEffectIndicatorByProcess?.length || 0;
        for (let i = 0; i < indicatorProcessCount; i++) {
          scheduleFitCapture(`${RATIONALIZATION_IMPROVEMENT_INDICATOR_PROCESS_PREFIX}${i}`);
        }
        const costProcessCount = rationalizationInvestmentProfitLossData.improvementEffectCostByProcess?.length || 0;
        for (let i = 0; i < costProcessCount; i++) {
          scheduleFitCapture(`${RATIONALIZATION_IMPROVEMENT_COST_PROCESS_PREFIX}${i}`);
        }
        scheduleFitCapture(RATIONALIZATION_IMPROVEMENT_EFFECT_TOTAL_ID);
      }

      // 生産能力プロセス別チャート
      if (productionCapacityProcessChartDataList) {
        productionCapacityProcessChartDataList.forEach((_, i) => {
          scheduleFitCapture(`${PRODUCTION_CAPACITY_PROCESS_ITEM_PREFIX}${i}`);
        });
      }
    }

    // ベンチマーク
    if (refs.benchmarkRefs.current) {
      refs.benchmarkRefs.current.forEach((ref, i) => {
        if (ref) {
          captureJobs.push({ key: `benchmark-${i}`, factory: () => convertHtmlToImageJpeg(ref, 2, 1466) });
        }
      });
    }

    // 確認者
    scheduleTableCapture(CONFIRMER_LIST_BOX_PREFIX);

    // 承認履歴
    scheduleTableCapture(APPROVAL_LIST_BOX_PREFIX);

    // ReactFlow: ブラウザ幅に依存しないよう固定幅を設定してfitViewを再実行
    let reactflowOriginalWidth = '';
    if (refs.equipmentFlowRef.current) {
      const reactflowEl = refs.equipmentFlowRef.current as HTMLElement;
      reactflowOriginalWidth = reactflowEl.style.width;
      reactflowEl.style.width = '1466px';
      if (refs.reactFlowRef.current) {
        await refs.reactFlowRef.current.fitView();
        // fitViewアニメーション完了を待つ
        await new Promise<void>(resolve => setTimeout(resolve, 300));
      }
      captureJobs.push({ key: 'reactflow', factory: () => convertHtmlToImagePng(reactflowEl, 3) });
    }

    // Simulation: パディングと幅を事前設定してキャプチャジョブに追加
    // html-to-image (toJpeg) はドキュメント全体をクローンしないため html2canvas より大幅に高速
    const simOriginalStyles: { el: HTMLElement; originalPadding: string; originalWidth: string }[] = [];
    const chartContainerOriginalStyles: { el: HTMLElement; width: string; height: string }[] = [];
    // 損益非表示モード時はSimulationの表・グラフを出力しないためキャプチャ対象を空にする
    const simRefEntries = isProfitLossHiddenMode ? [] : [
      { ref: refs.simulationRef, key: 'simulation-0' },
      { ref: refs.simulationRef2, key: 'simulation-1' },
      { ref: refs.simulationRef3, key: 'simulation-2' },
    ];

    // PDF出力用の固定チャートサイズ（ブラウザサイズに依存しないよう固定値を使用）
    // 30vw相当（1920pxビューポート時）を固定ピクセルで指定
    const PDF_CHART_WIDTH = 576;
    const PDF_CHART_HEIGHT = Math.round(PDF_CHART_WIDTH * 0.5625); // 16:9アスペクト比 = 324px

    simRefEntries.forEach(({ ref, key }) => {
      if (ref.current) {
        const el = ref.current as HTMLElement;
        simOriginalStyles.push({ el, originalPadding: el.style.paddingBottom, originalWidth: el.style.width });
        el.style.paddingBottom = '16px';
        el.style.width = '1466px';

        // チャートコンテナ（30vw指定のBox）を固定サイズに変更
        const canvases = el.querySelectorAll('canvas');
        canvases.forEach(canvas => {
          const container = canvas.parentElement;
          if (container) {
            chartContainerOriginalStyles.push({
              el: container,
              width: container.style.width,
              height: container.style.height,
            });
            container.style.width = `${PDF_CHART_WIDTH}px`;
            container.style.height = `${PDF_CHART_HEIGHT}px`;
          }
        });

        captureJobs.push({ key, factory: () => convertHtmlToImageJpeg(el, 2, 1466) });
      }
    });

    // Chart.jsインスタンスを取得してcanvasの再描画を強制
    simRefEntries.forEach(({ ref }) => {
      if (ref.current) {
        const el = ref.current as HTMLElement;
        const canvases = el.querySelectorAll('canvas');
        canvases.forEach(canvas => {
          const chartInstance = ChartJS.getChart(canvas as HTMLCanvasElement);
          if (chartInstance) {
            chartInstance.resize();
          }
        });
      }
    });

    // Chart.jsの再描画完了を待つ
    await new Promise<void>(resolve => setTimeout(resolve, 500));

    // 全キャプチャを逐次実行する。
    // html-to-image (toJpeg) は内部で document 全体をクローンする処理を持ち、Promise.all で並列実行すると
    // メインスレッドで競合して 1 ジョブ ~60 秒に劣化するため、並列度 1（逐次）が最速となる。
    const captureResults: (string | null)[] = [];
    for (const job of captureJobs) {
      captureResults.push(await job.factory());
    }
    const imageCache = new Map<string, string | null>();
    captureJobs.forEach((job, idx) => imageCache.set(job.key, captureResults[idx]));

    // Simulation: チャートコンテナのスタイル復元
    chartContainerOriginalStyles.forEach(({ el, width, height }) => {
      el.style.width = width;
      el.style.height = height;
    });
    // Simulation: 親コンテナのスタイル復元
    simOriginalStyles.forEach(({ el, originalPadding, originalWidth }) => {
      el.style.paddingBottom = originalPadding;
      el.style.width = originalWidth;
    });
    // Chart.jsを元のサイズに再描画
    simRefEntries.forEach(({ ref }) => {
      if (ref.current) {
        const canvases = (ref.current as HTMLElement).querySelectorAll('canvas');
        canvases.forEach(canvas => {
          const chartInstance = ChartJS.getChart(canvas as HTMLCanvasElement);
          if (chartInstance) {
            chartInstance.resize();
          }
        });
      }
    });
    // ReactFlow: スタイル復元
    if (refs.equipmentFlowRef.current) {
      (refs.equipmentFlowRef.current as HTMLElement).style.width = reactflowOriginalWidth;
      if (refs.reactFlowRef.current) {
        refs.reactFlowRef.current.fitView();
      }
    }
    // ページフィット系: 幅復元
    fitCaptureOriginalStyles.forEach(({ el, originalWidth }) => {
      el.style.width = originalWidth;
    });

    // キャッシュから画像を取得するヘルパー
    const getCachedImage = (key: string): string | null => {
      return imageCache.get(key) ?? null;
    };

    // ===== PDF組み立てフェーズ =====

    const pdf = new jsPDF('landscape', 'mm', 'a4') as jsPDFCustom;

    // フォントを登録（初回: addFileToVFS+addFont実行してvFSキャッシュ。2回目以降: vFSコピー+addFont）
    await ensureFontRegistered(pdf);
    pdf.setFont('NotoSansJP');


    const headerHeight = 20;
    const headerImageWidth = 50;
    const headerImageHeight = 10;

    // ヘッダーにロゴを追加
    const addHeader = () => {
      pdf.addImage(headerLogo, 'PNG', pdf.internal.pageSize.getWidth() - headerImageWidth - 10, 5, headerImageWidth, headerImageHeight);
      pdf.setDrawColor(0, 0, 128);
      pdf.setLineWidth(2);
      pdf.line(10, headerHeight, pdf.internal.pageSize.getWidth() - 10, headerHeight);
    };

    // 既存のaddPageメソッド
    const originalAddPage = pdf.addPage;
    // ページ番号を追加するためのカスタムaddPageメソッド
    pdf.addPage = function () {
      // ページ番号を追加
      const pageCount = this.internal.pages.length - 1;
      const pageWidth = this.internal.pageSize.width;
      const pageNoPosition = pageWidth - 25;
      this.setFontSize(10);
      this.setPage(pageCount);
      this.text(`Page ${pageCount}`, pageNoPosition, 205);

      // オリジナルのaddPageメソッドを呼び出す
      const result = originalAddPage.call(this, [...arguments]);
      // jsPDFオブジェクトを返す
      return result;
    };

    // ヘッダ部レンダリング
    const renderHeader = (text: string | string[]) => {
      pdf.setFontSize(16);
      pdf.text(text, 14, 12);
    };

    // テキストを指定した幅に収まるように分割する関数
    const splitTextToSize = (text: string, maxWidth: number, doc: jsPDF) => {
      const lines = doc.splitTextToSize(text, maxWidth);
      return lines.length > 0 ? lines : [text];
    };
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 10;
    const maxLineWidth = pageWidth - margin * 2;

    // 改ページが必要かを判定し、必要なら改ページを行う関数
    const judgeAddPage = (lineSpacing: number, resetFontSize: number, resetY: number) => {
      let isAddPage = false;
      if (finalY + lineSpacing > pdf.internal.pageSize.getHeight() - 30) {
        addHeader();
        pdf.addPage();
        pdf.setFontSize(resetFontSize);
        finalY = resetY; // 描画位置をリセット
        isAddPage = true;
      }
      return isAddPage;
    };

    /** ---------- 共通処理 ---------- */
    // 事前キャプチャ済み画像をPDFに追加する（キャッシュヒット時はawait不要で高速）
    const addTableAsImage = async (pdf: jsPDFCustom, tableElement: HTMLElement, startY: number) => {
      try {
        // キャッシュから取得を試みる（事前キャプチャ済みの場合は即座に返る）
        const cachedUrl = tableElement.id ? getCachedImage(tableElement.id) : null;
        const fallbackWidth = Math.max(DEFAULT_CAPTURE_WIDTH, tableElement.scrollWidth);
        const dataUrl = cachedUrl ?? await convertHtmlToImageJpeg(tableElement, 2, fallbackWidth);
        if (!dataUrl) {
          throw new Error('Data URL is null or undefined');
        }
        const imgProps = pdf.getImageProperties(dataUrl);
        const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width; // アスペクト比を保つ
        pdf.addImage(dataUrl, 'JPEG', 10, startY, pdfWidth, pdfHeight);
        return startY + pdfHeight; // 次の開始位置を返す
      } catch (error) {
        console.error('Error capturing image:', error);
      }
    };


    /** ---------- 表紙 ---------- */
    const currencyName = selectedCurrency.name;
    // amountプロパティがundefinedでないオブジェクトのみをフィルタリング
    const validEquipmentAmounts = convEquipmentAmounts.filter((item) => item.amount !== undefined) as { amount: string; applicationPlannedMonth?: string }[];
    pdf.setFontSize(24);
    const projectName = projectData?.projectName ?? '';
    const projectNameLines = splitTextToSize(projectName, maxLineWidth, pdf);
    // 分割されたテキストを個別に描画
    // 高さを計算( ページの高さ / 2 ) - ( ( プロジェクト名行数 * 10[フォントの高さ] ) / 2 )
    const startYPrj = (pdf.internal.pageSize.getHeight() / 2) - ((projectNameLines.length * 10) / 2);
    projectNameLines.forEach((line: string, index: number) => {
      pdf.text(line, pageWidth / 2, startYPrj + (index * 10), { align: 'center' });
    });
    // 申請月度
    // ※申請月度が空でくることはないが念のため空か判定してから設定
    if (projectData?.applicationMonth !== null) {
      const applicationMon = dayjs(projectData?.applicationMonth).format(
        i18n.t('common.dateFormat.dayjsYearMonthDegree')
      );
      pdf.text(applicationMon, pageWidth / 2, startYPrj - 10, { align: 'center' });
    }

    pdf.setFontSize(12);
    // 会社/工場：複数選択対応（件数に応じて表紙下部テキスト群全体を上にシフト）
    const cpSnapshot = parseCompanyPlantSnapshot(projectData?.companyPlantSnapshot);
    const cpLineHeight = 6;
    const cpCount = cpSnapshot && cpSnapshot.items.length > 0 ? cpSnapshot.items.length : 1;
    // 2件目以降の追加行数分だけ全テキストを上にシフトするオフセット
    const cpOffset = (cpCount - 1) * cpLineHeight;
    const pageH = pdf.internal.pageSize.getHeight();

    pdf.text(`${i18n.t('features.InvestmentScreening.pdf.label.applicationDivision')}　: ${getDivisionHierarchyDisplay(projectData) || projectData?.divisionName}`, 14, pageH - 58 - cpOffset);
    pdf.text(`${i18n.t('features.InvestmentScreening.pdf.label.registrant')}　　: ${projectData?.registrantEmployeeName ?? ''}`, 14, pageH - 52 - cpOffset);
    pdf.text(`${i18n.t('features.InvestmentScreening.pdf.label.applicant')}　　: ${executiveManager[0]?.employeeName ?? ''}`, 14, pageH - 46 - cpOffset);
    pdf.text(`${i18n.t('features.InvestmentScreening.pdf.label.inspectionDateShort')}: ${projectData && projectData.projectStartDate ? dayjs(projectData.projectStartDate).format(i18n.t('common.dateFormat.dayjsFullDate')) : ""}`, 14, pageH - 40 - cpOffset);
    pdf.text(`${i18n.t('features.InvestmentScreening.pdf.label.applicationNumberShort')}　: ${projectData?.applicationNumber || ''}`, 14, pageH - 34 - cpOffset);
    pdf.text(`${i18n.t('features.InvestmentScreening.pdf.label.recordingCurrency')}　: ${i18n.t('features.InvestmentScreening.pdf.message.currencyAndRate', { currency: currencyNameFilter(currencyData, projectData), rate: formatExchangeRate(currencyRateFilter(currencyData, projectData) ?? undefined) })}${rateType && ratePeriod ? ` (${rateType === 'actual' ? i18n.t('features.InvestmentScreening.pdf.label.actualRate') : i18n.t('features.InvestmentScreening.pdf.label.plannedRate')} ${rateType === 'actual' ? i18n.t('features.InvestmentScreening.pdf.message.yearMonth', { year: ratePeriod.substring(0, 4), month: ratePeriod.substring(5, 7) }) : i18n.t('features.InvestmentScreening.pdf.message.fiscalYear', { year: ratePeriod })})` : ''}`, 14, pageH - 28 - cpOffset);
    pdf.text(`${i18n.t('features.InvestmentScreening.pdf.label.requestedAmountShort')}　: ${getRequestedAmount(validEquipmentAmounts, currencyName, projectData?.applicationMonth)}`, 14, pageH - 22 - cpOffset);
    pdf.text(`${i18n.t('features.InvestmentScreening.pdf.label.purposeShort')}　: ${projectData?.purpose ? getPurposeTypeNameByCode(projectData.purpose) : ''}`, 14, pageH - 16 - cpOffset);
    if (!cpSnapshot || cpSnapshot.items.length === 0) {
      pdf.text(`${i18n.t('features.InvestmentScreening.pdf.label.companyPlant')} :`, 14, pageH - 10);
    } else {
      cpSnapshot.items.forEach((item, idx) => {
        const label = idx === 0 ? `${i18n.t('features.InvestmentScreening.pdf.label.companyPlant')}   :` : '　　　　　:';
        const value = `${getBilingualDisplayName(item, 'companyName', 'companyNameEn')}${item.plantName ? ` / ${getBilingualDisplayName(item, 'plantName', 'plantNameEn')}` : ''}`;
        pdf.text(`${label} ${value}`, 14, pageH - 10 - cpOffset + idx * cpLineHeight);
      });
    }
    // ヘッダーを追加
    addHeader();
    // 新しいページを追加
    pdf.addPage();

    /** 申請まとめ */
    pdf.setFontSize(16);
    renderHeader(i18n.t('features.InvestmentScreening.pdf.title.equipmentApplicationSummary'));

    // 会社/工場の複数選択対応：items を "会社名 / 工場名" で改行連結
    const cpSnapshotForSummary = parseCompanyPlantSnapshot(projectData?.companyPlantSnapshot);
    const companyPlantText = cpSnapshotForSummary && cpSnapshotForSummary.items.length > 0
      ? cpSnapshotForSummary.items
        .map(item => `${item.companyName || ''}${item.plantName ? ` / ${item.plantName}` : ''}`)
        .join('\n')
      : '';

    const summaryData = [
      [i18n.t('features.InvestmentScreening.pdf.label.caseName'), projectData?.revisedOpportunityNameByRevision ? projectData.revisedOpportunityNameByRevision : projectData?.opportunityName],
      [i18n.t('features.InvestmentScreening.pdf.label.division'), getDivisionHierarchyDisplay(projectData) || projectData?.divisionName],
      [i18n.t('features.InvestmentScreening.pdf.label.productName'), projectData?.customerProductName],
      [i18n.t('features.InvestmentScreening.pdf.label.customer'), splitEscapedList(projectData?.endUserName).join(" / ")],
      [i18n.t('features.InvestmentScreening.pdf.label.companyPlant'), companyPlantText],
      [i18n.t('features.InvestmentScreening.pdf.label.baseInterestRate'), projectData?.baseInterestRate != null ? `${projectData.baseInterestRate}%` : ''],
      [i18n.t('features.InvestmentScreening.pdf.label.productionLineName'), productionLineData?.productionLineName],
      [i18n.t('features.InvestmentScreening.pdf.label.equipmentPlanNumber'), Array.isArray(projectData?.equipmentPlanNumber)
        ? projectData.equipmentPlanNumber.join(", ")
        : projectData?.equipmentPlanNumber],
      [i18n.t('features.InvestmentScreening.pdf.label.plannedBudget'), projectData?.budgetAmount
        ? `${projectData.budgetAmount.toLocaleString()} JPY（${getPlannedBudgetTypeNameByCode(projectData.plannedBudget ?? "")}）`
        : getPlannedBudgetTypeNameByCode(projectData?.plannedBudget ?? "")],
      [i18n.t('features.InvestmentScreening.pdf.label.equipmentCategory'), projectData?.equipmentCategory ? getEquipmentCategoryNamesByCodes(projectData.equipmentCategory) : ''],
      [i18n.t('features.InvestmentScreening.pdf.label.purpose'), projectData?.purpose ? getPurposeTypeNameByCode(projectData.purpose) : ''],
      [i18n.t('features.InvestmentScreening.pdf.label.supplierMaker'), uniqueSupplierNames],
      [i18n.t('features.InvestmentScreening.pdf.label.requestedAmount'), getRequestedAmount(validEquipmentAmounts, currencyName, projectData?.applicationMonth)],
      // EOL予定日（設備の実際のEOL予定日。未設定の既存案件は計上期間終了日(projectEndDate)をフォールバック表示）
      [i18n.t('features.InvestmentScreening.pdf.label.eolDate'), (() => {
        const eolDateValue = projectData?.eolDate ?? projectData?.projectEndDate;
        return eolDateValue ? dayjs(eolDateValue).format(i18n.t('common.dateFormat.dayjsFullDate')) : "";
      })()],
      [i18n.t('features.InvestmentScreening.pdf.label.inspectionDate'), projectData && projectData.projectStartDate ? dayjs(projectData.projectStartDate).format(i18n.t('common.dateFormat.dayjsFullDate')) : ""],
      [i18n.t('features.InvestmentScreening.pdf.label.paybackPeriod'), dispProfitAndLoss.paybackPeriodByYear?.paybackPeriod ? `${dispProfitAndLoss.paybackPeriodByYear.paybackPeriod}` : ""],
      [i18n.t('features.InvestmentScreening.pdf.label.downsideRisk'), projectData?.downsideRisk]
    ];

    let finalY = 25; // Y軸初期値設定
    const sanitizedSummaryData = summaryData.map(row =>
      row.map(cell => cell !== undefined ? cell : null)
    );

    autoTable(pdf, {
      body: sanitizedSummaryData,
      startY: 25,
      margin: { top: 25 },
      theme: 'striped',
      styles: {
        font: "NotoSansJP",
        fontStyle: "normal",
        fontSize: 14,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240],
      },
      columnStyles: {
        0: { cellWidth: 70 },// 最初の列（ヘッダー列）の幅を指定
        1: { cellWidth: 'auto' } // 2列目の幅を自動調整
      },
      didDrawPage: (data: any) => {
        addHeader();
        finalY = data.cursor.y; // 最終位置を取得
      }
    });
    pdf.addPage();

    /** ---------- 概要 ---------- */
    pdf.setFontSize(16);
    renderHeader(i18n.t('features.InvestmentScreening.pdf.title.overview'));

    // 製品画像を追加
    const img = new Image();
    if (uploadedImageFileContent !== null) {
      img.src = uploadedImageFileContent;
      await new Promise((resolve) => img.onload = resolve);
      const { scaledWidth, scaledHeight, xOffset, yOffset } = scaleImage(img, 50, 30);
      pdf.addImage(uploadedImageFileContent, 'PNG', 14 + xOffset, 45 + yOffset, scaledWidth, scaledHeight);
    }
    const overviewData = [
      [i18n.t('features.InvestmentScreening.pdf.label.productName'), projectData?.customerProductName],
      [i18n.t('features.InvestmentScreening.pdf.label.finalCustomer'), splitEscapedList(projectData?.endUserName).join(" / ")],
      [i18n.t('features.InvestmentScreening.pdf.label.customerIndustry'), projectData?.customerIndustryHierarchy],
      [i18n.t('features.InvestmentScreening.pdf.label.application'), projectData?.applicationCategory],
      [i18n.t('features.InvestmentScreening.pdf.label.carModel'), projectData?.carModel],
      [i18n.t('features.InvestmentScreening.pdf.label.specAndPerformance'), projectData?.specificationAndPerformance]
    ];
    // overviewData の要素が undefined を含まないように変換
    const sanitizedOverviewData = overviewData.map(row =>
      row.map(cell => cell !== undefined ? cell : null)
    );

    autoTable(pdf, {
      head: [['', '']],
      body: sanitizedOverviewData,
      startY: 30,
      margin: { top: 30, left: 70 }, // 画像の右にテーブルを配置
      theme: 'striped',
      styles: {
        font: "NotoSansJP",
        fontStyle: "normal",
        fontSize: 14,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240],
      },
      didDrawPage: (data: any) => {
        addHeader();
        finalY = data.cursor.y;
      }
    });

    // 投資目的セクション
    const pagewidth = pdf.internal.pageSize.getWidth()    // PDFの横幅を取得
    const pageHeight = pdf.internal.pageSize.getHeight()  // PDFの縦幅を取得
    const Margin = 14;                                    // 余白(x軸の描画開始位置)
    let CurrentY = finalY + 25;                           // y軸の描画開始位置

    const wrapText = (
      text: string,       // 描画するテキスト
      x: number,          // 横方向の描画開始位置
      y: number,          // 縦方向の描画開始位置
      fontSize: number,   // フォントサイズ
      lineHeight: number  // 行間幅
    ) => {
      const lines = pdf.splitTextToSize(text, pagewidth); // 折り返し処理

      lines.forEach((line: string) => {
        if (y + lineHeight > pageHeight - Margin) {
          pdf.addPage();
          addHeader();
          y = 40;
        }
        pdf.setFontSize(fontSize);
        pdf.text(line, x, y);
        y += lineHeight;
      });


      return y;
    };

    pdf.setFontSize(16);
    pdf.text(i18n.t('features.InvestmentScreening.pdf.label.investmentPurpose'), 14, finalY + 10);
    wrapText(`${projectData?.purposeOfInvestment ?? ""}`, Margin, CurrentY, 14, 10);

    /** ---------- 顧客取り決め事項 ---------- */
    pdf.addPage();
    addHeader();
    pdf.setFontSize(16);
    renderHeader(i18n.t('features.InvestmentScreening.pdf.title.pricingAgreement'));

    finalY = 20;
    // pricingAgreementSentenceが存在する場合のみ出力
    if (pricingAgreementSentence) {
      const leftMargin = 14;
      const labelWidth = 100;
      const lineSpacing = 10; // 1行あたりの高さ
      const colonMargin = 102; // 「:」の位置
      // 単価の取り決め
      if (pricingAgreementSentence?.pricing) {
        pdf.text(`${PricingAgreementCategory.pricing}`, leftMargin, finalY + lineSpacing);
        pdf.text(`:`, colonMargin, finalY + lineSpacing);
        pdf.text(`${pricingAgreementSentence.pricing}`, leftMargin + labelWidth, finalY + lineSpacing);
        finalY += 10;
      }
      // 単価の取り決め 自由記入欄
      if (pricingAgreementSentence?.pricingComment) {
        const pricingCommentLabel = `${PricingAgreementCategory.pricing} ${i18n.t('features.InvestmentScreening.pdf.label.freeText')}`;
        pdf.text(pricingCommentLabel, leftMargin, finalY + lineSpacing);
        const pricingCommentColonMargin = Math.max(colonMargin, leftMargin + pdf.getTextWidth(pricingCommentLabel) + 2);
        pdf.text(`:`, pricingCommentColonMargin, finalY + lineSpacing);
        const pricingCommentLabelWidth = Math.max(labelWidth, pricingCommentColonMargin - leftMargin + 8);
        const pricingCommentLines = pdf.splitTextToSize(`${pricingAgreementSentence.pricingComment}`,
          pdf.internal.pageSize.getWidth() - leftMargin - pricingCommentLabelWidth - 14
        );
        for (let i = 0; i < pricingCommentLines.length; i++) {
          // １行ずつ文字列をPDFに設定
          pdf.text(pricingCommentLines[i], leftMargin + pricingCommentLabelWidth, finalY + lineSpacing);
          // 次の行が余白を超えるか確認し、超える場合は改ページする
          finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
        }
      }
      // 為替変動の取り決め
      if (pricingAgreementSentence?.exchange) {
        pdf.text(`${PricingAgreementCategory.exchange}`, leftMargin, finalY + lineSpacing);
        pdf.text(`:`, colonMargin, finalY + lineSpacing);
        pdf.text(`${pricingAgreementSentence.exchange}`, leftMargin + labelWidth, finalY + lineSpacing);
        // 次の行が余白を超えるか確認し、超える場合は改ページする
        finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
      }
      // 為替変動の取り決め 自由記入欄
      if (pricingAgreementSentence?.exchangeComment) {
        const exchangeCommentLabel = `${PricingAgreementCategory.exchange} ${i18n.t('features.InvestmentScreening.pdf.label.freeText')}`;
        pdf.text(exchangeCommentLabel, leftMargin, finalY + lineSpacing);
        const exchangeCommentColonMargin = Math.max(colonMargin, leftMargin + pdf.getTextWidth(exchangeCommentLabel) + 2);
        pdf.text(`:`, exchangeCommentColonMargin, finalY + lineSpacing);
        const exchangeCommentLabelWidth = Math.max(labelWidth, exchangeCommentColonMargin - leftMargin + 8);
        const exchangeCommentLines = pdf.splitTextToSize(`${pricingAgreementSentence.exchangeComment}`,
          pdf.internal.pageSize.getWidth() - leftMargin - exchangeCommentLabelWidth - 14
        );
        for (let i = 0; i < exchangeCommentLines.length; i++) {
          // １行ずつ文字列をPDFに設定
          pdf.text(exchangeCommentLines[i], leftMargin + exchangeCommentLabelWidth, finalY + lineSpacing);
          // 次の行が余白を超えるか確認し、超える場合は改ページする
          finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
        }
      }
      // 材料費変動の取り決め
      if (pricingAgreementSentence?.material || pricingAgreementSentence?.selectMaterial) {
        pdf.text(`${PricingAgreementCategory.material}`, leftMargin, finalY + lineSpacing);
        pdf.text(`:`, colonMargin, finalY + lineSpacing);
        const materialText = `${pricingAgreementSentence.material || ''} ${pricingAgreementSentence.selectMaterial || ''}`;
        const materialLines = pdf.splitTextToSize(materialText, pdf.internal.pageSize.getWidth() - leftMargin - labelWidth - 14);
        for (let i = 0; i < materialLines.length; i++) {
          // １行ずつ文字列をPDFに設定
          pdf.text(materialLines[i], leftMargin + labelWidth, finalY + lineSpacing);
          // 次の行が余白を超えるか確認し、超える場合は改ページする
          finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
        }
      }
      // 材料費変動の取り決め 自由記入欄
      if (pricingAgreementSentence?.materialComment) {
        const materialCommentLabel = `${PricingAgreementCategory.material} ${i18n.t('features.InvestmentScreening.pdf.label.freeText')}`;
        pdf.text(materialCommentLabel, leftMargin, finalY + lineSpacing);
        const materialCommentColonMargin = Math.max(colonMargin, leftMargin + pdf.getTextWidth(materialCommentLabel) + 2);
        pdf.text(`:`, materialCommentColonMargin, finalY + lineSpacing);
        const materialCommentLabelWidth = Math.max(labelWidth, materialCommentColonMargin - leftMargin + 8);
        const materialCommentLines = pdf.splitTextToSize(`${pricingAgreementSentence.materialComment}`,
          pdf.internal.pageSize.getWidth() - leftMargin - materialCommentLabelWidth - 14
        );
        for (let i = 0; i < materialCommentLines.length; i++) {
          // １行ずつ文字列をPDFに設定
          pdf.text(materialCommentLines[i], leftMargin + materialCommentLabelWidth, finalY + lineSpacing);
          // 次の行が余白を超えるか確認し、超える場合は改ページする
          finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
        }
      }
      // 数量の取り決め
      if (pricingAgreementSentence?.quantity) {
        pdf.text(`${PricingAgreementCategory.quantity}`, leftMargin, finalY + lineSpacing);
        pdf.text(`:`, colonMargin, finalY + lineSpacing);
        pdf.text(`${pricingAgreementSentence.quantity}`, leftMargin + labelWidth, finalY + lineSpacing);
        // 次の行が余白を超えるか確認し、超える場合は改ページする
        finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
      }
      // 数量未達時の取り決め
      if (pricingAgreementSentence?.notReachedComment) {
        pdf.text(`${PricingAgreementCategory.quantityNotReached}`, leftMargin, finalY + lineSpacing);
        pdf.text(`:`, colonMargin, finalY + lineSpacing);
        const notReachedCommentLines = pdf.splitTextToSize(`${pricingAgreementSentence.notReachedComment}`,
          pdf.internal.pageSize.getWidth() - leftMargin - labelWidth - 14
        );
        for (let i = 0; i < notReachedCommentLines.length; i++) {
          // １行ずつ文字列をPDFに設定
          pdf.text(notReachedCommentLines[i], leftMargin + labelWidth, finalY + lineSpacing);
          // 次の行が余白を超えるか確認し、超える場合は改ページする
          finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
        }
      }
      // 数量の取り決め 自由記入欄
      if (pricingAgreementSentence?.quantityComment) {
        const quantityCommentLabel = `${PricingAgreementCategory.quantity} ${i18n.t('features.InvestmentScreening.pdf.label.freeText')}`;
        pdf.text(quantityCommentLabel, leftMargin, finalY + lineSpacing);
        const quantityCommentColonMargin = Math.max(colonMargin, leftMargin + pdf.getTextWidth(quantityCommentLabel) + 2);
        pdf.text(`:`, quantityCommentColonMargin, finalY + lineSpacing);
        const quantityCommentLabelWidth = Math.max(labelWidth, quantityCommentColonMargin - leftMargin + 8);
        const quantityCommentLines = pdf.splitTextToSize(`${pricingAgreementSentence.quantityComment}`,
          pdf.internal.pageSize.getWidth() - leftMargin - quantityCommentLabelWidth - 14
        );
        for (let i = 0; i < quantityCommentLines.length; i++) {
          // １行ずつ文字列をPDFに設定
          pdf.text(quantityCommentLines[i], leftMargin + quantityCommentLabelWidth, finalY + lineSpacing);
          // 次の行が余白を超えるか確認し、超える場合は改ページする
          finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
        }
      }

      // 顧客取り決め事項 競合表示
      // 数量の取り決め 自由記入欄で改ページ済、１行目からの開始の場合以外は改ページする
      if (finalY != 20) {
        addHeader();
        pdf.addPage();
        pdf.setFontSize(16);
        finalY = 20;
      }
      // 競合の有無
      if (pricingAgreementSentence?.competitor) {
        pdf.text(`${PricingAgreementCategory.competitor}`, leftMargin, finalY + lineSpacing);
        pdf.text(`:`, colonMargin, finalY + lineSpacing);
        pdf.text(`${pricingAgreementSentence.competitor}`, leftMargin + labelWidth, finalY + lineSpacing);
        // 次の行が余白を超えるか確認し、超える場合は改ページする
        finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
      }
      // 競合状態
      if (pricingAgreementSentence?.competitiorCondition) {
        pdf.text(`${PricingAgreementCategory.competitiorCondition}`, leftMargin, finalY + lineSpacing);
        pdf.text(`:`, colonMargin, finalY + lineSpacing);
        pdf.text(`${pricingAgreementSentence.competitiorCondition}`, leftMargin + labelWidth, finalY + lineSpacing);
        // 次の行が余白を超えるか確認し、超える場合は改ページする
        finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
      }
      // 競合会社選択
      if (pricingAgreementSentence?.selectCompetitor) {
        pdf.text(`${PricingAgreementCategory.selectedCompetitors}`, leftMargin, finalY + lineSpacing);
        pdf.text(`:`, colonMargin, finalY + lineSpacing);
        const selectedCompetitorsLines = pdf.splitTextToSize(`${pricingAgreementSentence.selectCompetitor}`,
          pdf.internal.pageSize.getWidth() - leftMargin - labelWidth - 14
        );
        for (let i = 0; i < selectedCompetitorsLines.length; i++) {
          // １行ずつ文字列をPDFに設定
          pdf.text(selectedCompetitorsLines[i], leftMargin + labelWidth, finalY + lineSpacing);
          // 次の行が余白を超えるか確認し、超える場合は改ページする
          finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
        }
      }
      // 当社シェア
      if (pricingAgreementSentence?.sharePercentage != null) {
        pdf.text(i18n.t('features.InvestmentScreening.pdf.label.companyShare'), leftMargin, finalY + lineSpacing);
        pdf.text(`:`, colonMargin, finalY + lineSpacing);
        pdf.text(`${pricingAgreementSentence.sharePercentage}%`, leftMargin + labelWidth, finalY + lineSpacing);
        finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
      }
      // 当社及び競合のシェア
      if (pricingAgreementSentence?.shareOfCompetitorAndCompany) {
        pdf.text(`${PricingAgreementCategory.shareOfCompetitorAndCompany}`, leftMargin, finalY + lineSpacing);
        pdf.text(`:`, colonMargin, finalY + lineSpacing);
        const shareOfCompetitorAndCompanyLines = pdf.splitTextToSize(`${pricingAgreementSentence.shareOfCompetitorAndCompany}`,
          pdf.internal.pageSize.getWidth() - leftMargin - labelWidth - 14
        );
        for (let i = 0; i < shareOfCompetitorAndCompanyLines.length; i++) {
          // １行ずつ文字列をPDFに設定
          pdf.text(shareOfCompetitorAndCompanyLines[i], leftMargin + labelWidth, finalY + lineSpacing);
          // 次の行が余白を超えるか確認し、超える場合は改ページする
          finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
        }
      }
      // その他の取り決め
      if (pricingAgreementSentence?.otherAgreements && pricingAgreementSentence.otherAgreements.length > 0) {
        for (const item of pricingAgreementSentence.otherAgreements) {
          if (item.title || item.content) {
            // タイトルを折り返し
            const titleLines = pdf.splitTextToSize(
              item.title || i18n.t('features.InvestmentScreening.pdf.message.noTitle'),
              labelWidth - 14
            );
            // 内容を折り返し
            const contentLines = pdf.splitTextToSize(
              item.content || '',
              pdf.internal.pageSize.getWidth() - leftMargin - labelWidth - 14
            );
            const maxLines = Math.max(titleLines.length, contentLines.length);
            for (let i = 0; i < maxLines; i++) {
              if (i < titleLines.length) {
                pdf.text(titleLines[i], leftMargin, finalY + lineSpacing);
              }
              if (i === 0) {
                pdf.text(`:`, colonMargin, finalY + lineSpacing);
              }
              if (i < contentLines.length) {
                pdf.text(contentLines[i], leftMargin + labelWidth, finalY + lineSpacing);
              }
              finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
            }
          }
        }
      }
      // 顧客国籍
      if (customerNationalityName) {
        pdf.text(i18n.t('features.InvestmentScreening.pdf.label.customerNationality'), leftMargin, finalY + lineSpacing);
        pdf.text(`:`, colonMargin, finalY + lineSpacing);
        pdf.text(customerNationalityName, leftMargin + labelWidth, finalY + lineSpacing);
        finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
      }
      // アップロード済みファイル
      const files = uploadedFilesByFolder.find(f => f.folderName === "PricingAgreementForm")?.files;
      if (files) {
        pdf.text(`${PricingAgreementCategory.uploadedFile}`, leftMargin, finalY + lineSpacing);
        pdf.text(`:`, colonMargin, finalY + lineSpacing);
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const x = leftMargin + labelWidth;
          const y = finalY + lineSpacing;
          // マルウェア検知ファイルはリンクを張らず、ファイル名と注記のみ記載する
          const downloadable = !!file.url && !isScanBlocked((file as any).scanStatus);
          const label = downloadable
            ? file.fileName
            : `${file.fileName}（${i18n.t('components.ScanStatusBadge.label.threat')}）`;
          if (downloadable) {
            pdf.setTextColor(0, 0, 255);
            pdf.textWithLink(label, x, y, { url: file.url });
            const textWidth = pdf.getTextWidth(label);
            pdf.setLineWidth(0.5);
            pdf.line(x, y + 1, x + textWidth, y + 1);
          } else {
            pdf.setTextColor(128, 128, 128);
            pdf.text(label, x, y);
          }
          pdf.setTextColor(0, 0, 0);
          // 次の行が余白を超えるか確認し、超える場合は改ページする
          finalY = judgeAddPage(lineSpacing, 16, 20) ? finalY : finalY += lineSpacing;
        }
      }
    }
    // アップロード済みファイルで改ページ済、１行目からの開始の場合以外は改ページする
    if (finalY != 20) {
      addHeader();
      pdf.addPage();
    }

    /** ---------- 販売数量・販売単価 ---------- */
    // P&Lセクション以降でも startY/currentY を参照するため、if ブロック外で宣言
    let startY = 45;
    let currentY = 30;
    if (isSimpleMode) {
      // 簡易モード時は見出しとメッセージのみ表示
      pdf.setFontSize(16);
      renderHeader(labelQuantityAndPrice);
      pdf.setFontSize(12);
      pdf.setTextColor(102, 102, 102);
      pdf.text(i18n.t('features.InvestmentScreening.pdf.message.simpleModeExcluded'), 14, 35);
      pdf.setTextColor(0, 0, 0);
      addHeader();
    } else {
      pdf.setFontSize(16);
      renderHeader(labelQuantityAndPrice);

      pdf.setFontSize(14);
      pdf.text(i18n.t('features.InvestmentScreening.pdf.label.salesForecast'), 14, 30);
      pdf.setFontSize(12);
      pdf.text(i18n.t('features.InvestmentScreening.pdf.message.currencyAndRate', { currency: selectedCurrency.name, rate: formatExchangeRate(currencyData.find(c => c.currencyId === selectedCurrency.id)?.exchangeRate) }), 14, 37);

      addHeader();

      startY = 45; // テーブルの開始位置を設定
      finalY = 20;
      const quantityAndPricePartNoTables = async () => {
        for (const [tableIndex, table] of quantityAndPriceTables.entries()) {

          if (tableIndex > 0 && tableIndex % 2 === 0) {
            addHeader();
            pdf.addPage();
            pdf.setFontSize(14);
            startY = 45; // 新しいページの開始位置をリセット
          }

          pdf.setFontSize(14);
          pdf.text(`PartNo: ${resolveDisplayPartNoByKey(table.partNo, partNoData)}`, 14, startY);
          pdf.setFontSize(12);
          pdf.text(i18n.t('features.InvestmentScreening.pdf.message.salesCurrencyAndRate', { currency: currencyNameFilter(currencyData, table.currencyAndRatio), rate: formatExchangeRate(currencyRateFilter(currencyData, table.currencyAndRatio) ?? undefined) }), 14, startY + 8);
          pdf.setFontSize(14);
          pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, startY + 16); // 通貨単位を表示

          // 数量・販売単価セクションのテーブルを画像で出力
          const quantityAndPriceSection = document.getElementById(`${QUANTITY_PRICE_PARTNO_TABLE_PREFIX}${tableIndex}`);
          if (quantityAndPriceSection) {
            quantityAndPriceSection.style.overflowX = 'visible';
            quantityAndPriceSection.style.maxWidth = 'none';
            const imagePos = await addTableAsImage(pdf, quantityAndPriceSection, startY + 22);
            if (imagePos !== undefined) {
              startY = imagePos;
            }
            startY += 14;
          }
        }
      };

      // 数量・販売単価 PartNoテーブルのキャプチャ
      await quantityAndPricePartNoTables().catch(error => {
        console.error('Error quantityAndPricePartNoTables:', error);
      });

      addHeader();
      pdf.addPage();

      // 金型、開発費（プロジェクト単位）、プロジェクト合計で使用する描画位置
      currentY = 30;

      // 金型（プロジェクト単位）
      if (moldSalesByProjectYear && moldSalesByProjectYear.length > 0) {
        pdf.setFontSize(16);
        pdf.text(i18n.t('features.InvestmentScreening.pdf.label.moldByProject'), 14, currentY);
        pdf.setFontSize(14);
        pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8); // 通貨単位を表示

        const quantityAndPriceMoldSection = document.getElementById(`${QUANTITY_PRICE_MOLD_TABLE_PREFIX}`);
        if (quantityAndPriceMoldSection) {
          quantityAndPriceMoldSection.style.overflowX = 'visible';
          quantityAndPriceMoldSection.style.maxWidth = 'none';
          const imagePos = await addTableAsImage(pdf, quantityAndPriceMoldSection, currentY + 12);
          if (imagePos !== undefined) {
            currentY = imagePos;
          }
          currentY += 14;
        }
      }

      // 開発費（プロジェクト単位）
      if (devSalesByProjectYear && devSalesByProjectYear.length > 0) {

        pdf.setFontSize(16);
        pdf.text(i18n.t('features.InvestmentScreening.pdf.label.devByProject'), 14, currentY);
        pdf.setFontSize(14);
        pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8); // 通貨単位を表示

        const quantityAndPriceDevSection = document.getElementById(`${QUANTITY_PRICE_DEV_TABLE_PREFIX}`);
        if (quantityAndPriceDevSection) {
          quantityAndPriceDevSection.style.overflowX = 'visible';
          quantityAndPriceDevSection.style.maxWidth = 'none';
          const imagePos = await addTableAsImage(pdf, quantityAndPriceDevSection, currentY + 12);
          if (imagePos !== undefined) {
            currentY = imagePos;
          }
          currentY += 14;
        }
      }

      // 設備費（プロジェクト単位）
      if (equipmentSalesByProjectYear && equipmentSalesByProjectYear.length > 0) {

        pdf.setFontSize(16);
        pdf.text(i18n.t('features.InvestmentScreening.pdf.label.equipmentByProject'), 14, currentY);
        pdf.setFontSize(14);
        pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8); // 通貨単位を表示

        const quantityAndPriceEquipmentSection = document.getElementById(`${QUANTITY_PRICE_EQUIPMENT_TABLE_PREFIX}`);
        if (quantityAndPriceEquipmentSection) {
          quantityAndPriceEquipmentSection.style.overflowX = 'visible';
          quantityAndPriceEquipmentSection.style.maxWidth = 'none';
          const imagePos = await addTableAsImage(pdf, quantityAndPriceEquipmentSection, currentY + 12);
          if (imagePos !== undefined) {
            currentY = imagePos;
          }
          currentY += 14;
        }
      }

      pdf.setFontSize(16);
      pdf.text(i18n.t('features.InvestmentScreening.pdf.label.projectTotal'), 14, currentY);
      pdf.setFontSize(14);
      pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8); // 通貨単位を表示

      const quantityAndPriceTotalSection = document.getElementById(`${QUANTITY_PRICE_PROJECT_TOTAL_TABLE_PREFIX}`);
      if (quantityAndPriceTotalSection) {
        quantityAndPriceTotalSection.style.overflowX = 'visible';
        quantityAndPriceTotalSection.style.maxWidth = 'none';
        const imagePos = await addTableAsImage(pdf, quantityAndPriceTotalSection, currentY + 12);
        if (imagePos !== undefined) {
          currentY = imagePos;
        }
        currentY += 14;
        addHeader();
      }

      /**自由記入欄 */
      if (projectData?.quantityPriceFreeText) {
        pdf.addPage();
        addHeader();

        let freeTextY = headerHeight + 20;
        pdf.setFontSize(16);
        pdf.text(i18n.t('features.InvestmentScreening.pdf.label.freeText'), 14, freeTextY);

        wrapText(`${projectData?.quantityPriceFreeText ?? ""}`, Margin, freeTextY + 14, 14, 10);
      }

      pdf.addPage();

      /** 企画数量（EOL） */
      addHeader();
      pdf.setFontSize(16);
      pdf.text(i18n.t('features.InvestmentScreening.pdf.label.plannedQuantityEOL'), 14, headerHeight + 12); // ヘッダー分のスペース
      pdf.setFontSize(14);
      let eolStartY = headerHeight + 20; // 企画数量（EOL）セクションの開始位置を設定
      quantityAndPriceTables.forEach((table) => {
        pdf.text(`${resolveDisplayPartNoByKey(table.partNo, partNoData)}: ${formatNumber(calculateTotalQuantity(table), "")}`, 14, eolStartY);
        eolStartY += 10; // 次の行の位置を調整
      });
    } // end of isSimpleMode/else (販売数量・販売単価セクション)



    /** ---------- P&L ---------- */
    pdf.addPage();
    addHeader();
    renderHeader("P&L");

    if (isProfitLossHiddenMode) {
      // 損益非表示モード時は見出しとメッセージのみ表示
      pdf.setFontSize(12);
      pdf.setTextColor(102, 102, 102);
      pdf.text(i18n.t('common.message.profitLossHiddenSectionNote'), 14, headerHeight + 12);
      pdf.setTextColor(0, 0, 0);
    } else {
      startY = headerHeight + 12;

      // 簡易モード時のみ、最初のP&L配下コンテンツでP&Lページを再利用するためのフラグ
      let canReusePLPage = isSimpleMode;

      // PartNoごとの年次テーブル（簡易モード時はスキップ）
      const profitAndLossPartNoTables = async () => {
        if (isSimpleMode) return;
        if (partNoDataMap) {
          const entries = Array.from(partNoDataMap.entries());
          for (let tableIndex = 0; tableIndex < entries.length; tableIndex++) {
            const [partNo, data] = entries[tableIndex];

            if (tableIndex > 0) {
              addHeader();
              pdf.addPage();
              pdf.setFontSize(14);
              startY = headerHeight + 12; // 新しいページの開始位置をリセット
            }

            if (tableIndex === 0) { renderHeader("P&L"); }

            pdf.setFontSize(14);
            pdf.text(`PartNo: ${resolveDisplayPartNoByKey(partNo, partNoData)}`, 14, startY);
            pdf.setFontSize(12);
            pdf.text(i18n.t('features.InvestmentScreening.pdf.message.salesCurrencyAndRate', { currency: currencyNameFilter(currencyData, data.currencyAndRatio), rate: formatExchangeRate(currencyRateFilter(currencyData, data.currencyAndRatio) ?? undefined) }), 14, startY + 8);
            pdf.setFontSize(14);
            pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, startY + 16); // 通貨単位を表示

            // P&L PartNoのテーブルを画像で出力
            const ProfitAndLossSection = document.getElementById(`${PROFIT_AND_LOSS_PARTNO_TABLE_PREFIX}${tableIndex}`);

            if (ProfitAndLossSection) {
              ProfitAndLossSection.style.overflowX = 'visible';
              ProfitAndLossSection.style.maxWidth = 'none';
              const imagePos = await addTableAsImage(pdf, ProfitAndLossSection, startY + 22);
              if (imagePos !== undefined) {
                startY = imagePos;
              }
              startY += 4;
            }

            // 改ページ
            addHeader();
            pdf.addPage();
            pdf.setFontSize(14);
            startY = headerHeight + 12;
            // 参考 外注のみ
            pdf.text(`PartNo: ${resolveDisplayPartNoByKey(partNo, partNoData)}`, 14, startY);
            pdf.text(i18n.t('features.InvestmentScreening.pdf.message.referenceOutsourceOnly'), 14, startY + 8);
            const ProfitAndLossOutsourcSection = document.getElementById(`${PROFIT_AND_LOSS_PARTNO_OUTSOURC_TABLE_PREFIX}${tableIndex}`);
            if (ProfitAndLossOutsourcSection) {
              ProfitAndLossOutsourcSection.style.overflowX = 'visible';
              ProfitAndLossOutsourcSection.style.maxWidth = 'none';
              const imagePos = await addTableAsImage(pdf, ProfitAndLossOutsourcSection, startY + 12);
              if (imagePos !== undefined) {
                startY = imagePos;
              }
              startY += 8;
            }
            // 参考 内製のみ
            pdf.text(i18n.t('features.InvestmentScreening.pdf.message.referenceInhouseOnly'), 14, startY + 8);
            const ProfitAndLossInhouseSection = document.getElementById(`${PROFIT_AND_LOSS_PARTNO_INHOUSE_TABLE_PREFIX}${tableIndex}`);
            if (ProfitAndLossInhouseSection) {
              ProfitAndLossInhouseSection.style.overflowX = 'visible';
              ProfitAndLossInhouseSection.style.maxWidth = 'none';
              const imagePos = await addTableAsImage(pdf, ProfitAndLossInhouseSection, startY + 12);
              if (imagePos !== undefined) {
                startY = imagePos;
              }
            }
          }
        }
      };

      // P&L PartNoテーブルのキャプチャ
      await profitAndLossPartNoTables().catch(error => {
        console.error('Error profitAndLossPartNoTables:', error);
      });

      addHeader();

      // 金型、開発費（プロジェクト単位）がある場合（簡易モード時はスキップ）
      if (!isSimpleMode && ((moldSalesByProjectYear && moldSalesByProjectYear.length > 0)
        || (devSalesByProjectYear && devSalesByProjectYear.length > 0)
        || (equipmentSalesByProjectYear && equipmentSalesByProjectYear.length > 0))) {
        pdf.addPage();
        addHeader();
        currentY = headerHeight + 10;
      }

      // 金型（プロジェクト単位）（簡易モード時はスキップ: DOM要素が存在せず currentY が更新されないまま次のテキストと重なるため）
      if (!isSimpleMode && moldSalesByProjectYear && moldSalesByProjectYear.length > 0) {
        pdf.setFontSize(16);
        pdf.text(i18n.t('features.InvestmentScreening.pdf.label.moldByProject'), 14, currentY);
        pdf.setFontSize(14);
        pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8); // 通貨単位を表示

        const profitAndLossMoldSection = document.getElementById(`${PROFIT_AND_LOSS_MOLD_TABLE_PREFIX}`);
        if (profitAndLossMoldSection) {
          profitAndLossMoldSection.style.overflowX = 'visible';
          profitAndLossMoldSection.style.maxWidth = 'none';
          const imagePos = await addTableAsImage(pdf, profitAndLossMoldSection, currentY + 12);
          if (imagePos !== undefined) {
            currentY = imagePos;
          }
          currentY += 14;
        }
      }

      // 開発費（プロジェクト単位）（簡易モード時はスキップ）
      if (!isSimpleMode && devSalesByProjectYear && devSalesByProjectYear.length > 0) {
        pdf.setFontSize(16);
        pdf.text(i18n.t('features.InvestmentScreening.pdf.label.devByProject'), 14, currentY);
        pdf.setFontSize(14);
        pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8); // 通貨単位を表示

        const profitAndLossDevSection = document.getElementById(`${PROFIT_AND_LOSS_DEV_TABLE_PREFIX}`);
        if (profitAndLossDevSection) {
          profitAndLossDevSection.style.overflowX = 'visible';
          profitAndLossDevSection.style.maxWidth = 'none';
          const imagePos = await addTableAsImage(pdf, profitAndLossDevSection, currentY + 12);
          if (imagePos !== undefined) {
            currentY = imagePos;
          }
          currentY += 14;
        }
      }

      // 設備費（プロジェクト単位）（簡易モード時はスキップ）
      if (!isSimpleMode && equipmentSalesByProjectYear && equipmentSalesByProjectYear.length > 0) {
        pdf.setFontSize(16);
        pdf.text(i18n.t('features.InvestmentScreening.pdf.label.equipmentByProject'), 14, currentY);
        pdf.setFontSize(14);
        pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8); // 通貨単位を表示

        const profitAndLossEquipmentSection = document.getElementById(`${PROFIT_AND_LOSS_EQUIPMENT_TABLE_PREFIX}`);
        if (profitAndLossEquipmentSection) {
          profitAndLossEquipmentSection.style.overflowX = 'visible';
          profitAndLossEquipmentSection.style.maxWidth = 'none';
          const imagePos = await addTableAsImage(pdf, profitAndLossEquipmentSection, currentY + 12);
          if (imagePos !== undefined) {
            currentY = imagePos;
          }
          currentY += 14;
        }
      }

      // 年度別総計（更新投資の場合は年度別損益（更新前/更新後））
      // ※合理化投資の場合は「年度別損益（合理化前/後）」を別セクションで出力するためスキップ
      if (maxYears.length !== 0 && totalYearlyData && projectData?.purpose !== PurposeType.Rationalization.code) {

        // 省略モードではない場合、年度別損益 または 年度別総計を表示
        if (!isSimpleMode) {
          pdf.addPage();
          addHeader();

          currentY = headerHeight + 12;
          pdf.setFontSize(14);
          // 更新投資の場合はタイトルを変更
          const profitLossTitle = projectData?.purpose === PurposeType.UpdateAndOther.code
            ? i18n.t('features.InvestmentScreening.pdf.label.profitLossByYearUpdate')
            : i18n.t('features.InvestmentScreening.pdf.label.profitLossByYearTotal');
          pdf.text(profitLossTitle, 14, currentY);

          pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8); // 通貨単位を表示

          const profitAndLossGrandTotalSection = document.getElementById(`${PROFIT_AND_LOSS_GRAND_TOTAL_TABLE_PREFIX}`);
          if (profitAndLossGrandTotalSection) {
            profitAndLossGrandTotalSection.style.overflowX = 'visible';
            profitAndLossGrandTotalSection.style.maxWidth = 'none';
            // 更新投資の場合はページに収まるようにスケーリング（事前キャプチャ済み画像を使用）
            const dataUrl = getCachedImage(PROFIT_AND_LOSS_GRAND_TOTAL_TABLE_PREFIX) ?? await convertHtmlToImageJpeg(profitAndLossGrandTotalSection, 2);
            if (dataUrl) {
              const imgProps = pdf.getImageProperties(dataUrl);
              const pageWidth = pdf.internal.pageSize.getWidth() - 20;
              const pageHeight = pdf.internal.pageSize.getHeight() - (currentY + 12) - 10;
              const finalScale = calculateScaleToFitPage(imgProps.width, imgProps.height, pageWidth, pageHeight);
              const finalImgWidth = imgProps.width * finalScale;
              const finalImgHeight = imgProps.height * finalScale;
              const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
              pdf.addImage(dataUrl, 'JPEG', xOffset, currentY + 12, finalImgWidth, finalImgHeight);
            }
          }

          // 更新による改善効果（更新投資の場合のみ）- プロセスごとにページ分割
          if (projectData?.purpose === PurposeType.UpdateAndOther.code && updateInvestmentProfitLossData) {
            const processCount = updateInvestmentProfitLossData.improvementEffectByProcess?.length || 0;

            // 各プロセスを別ページに出力
            for (let i = 0; i < processCount; i++) {
              const processSection = document.getElementById(`${UPDATE_IMPROVEMENT_EFFECT_PROCESS_ITEM_PREFIX}${i}`);
              if (processSection) {
                pdf.addPage();
                addHeader();

                currentY = headerHeight + 12;
                pdf.setFontSize(14);
                const processName = updateInvestmentProfitLossData.improvementEffectByProcess?.[i]?.processName || i18n.t('features.InvestmentScreening.pdf.process.processNumber', { num: i + 1 });
                pdf.text(i18n.t('features.InvestmentScreening.pdf.process.updateImprovementEffect', { processName }), 14, currentY);
                pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8);

                const dataUrl = getCachedImage(`${UPDATE_IMPROVEMENT_EFFECT_PROCESS_ITEM_PREFIX}${i}`) ?? await convertHtmlToImageJpeg(processSection, 2);
                if (dataUrl) {
                  const imgProps = pdf.getImageProperties(dataUrl);
                  const pageWidth = pdf.internal.pageSize.getWidth() - 20;
                  const pageHeight = pdf.internal.pageSize.getHeight() - (currentY + 20) - 10;
                  const finalScale = calculateScaleToFitPage(imgProps.width, imgProps.height, pageWidth, pageHeight);
                  const finalImgWidth = imgProps.width * finalScale;
                  const finalImgHeight = imgProps.height * finalScale;
                  const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
                  pdf.addImage(dataUrl, 'JPEG', xOffset, currentY + 20, finalImgWidth, finalImgHeight);
                }
              }
            }
          }
        }

        // 簡易モードの場合は「改善効果（指標・費用）」を表示
        if (isSimpleMode) {

          // 更新による改善効果（指標）- 各工程を別ページに出力
          if (updateInvestmentProfitLossData) {
            const indicatorProcessCount = updateInvestmentProfitLossData.improvementEffectIndicatorByProcess?.length || 0;
            for (let i = 0; i < indicatorProcessCount; i++) {
              const processSection = document.getElementById(`${UPDATE_IMPROVEMENT_INDICATOR_PROCESS_PREFIX}${i}`);
              if (processSection) {
                // 簡易モード時、最初のコンテンツのみP&Lページを再利用（空白ページ防止）
                if (canReusePLPage) {
                  canReusePLPage = false;
                } else {
                  pdf.addPage();
                  addHeader();
                }

                currentY = headerHeight + 12;
                pdf.setFontSize(14);
                const processName = updateInvestmentProfitLossData.improvementEffectIndicatorByProcess[i]?.processName || i18n.t('features.InvestmentScreening.pdf.process.processNumber', { num: i + 1 });
                pdf.text(i18n.t('features.InvestmentScreening.pdf.process.updateImprovementEffectIndicator', { processName }), 14, currentY);

                const dataUrl = getCachedImage(`${UPDATE_IMPROVEMENT_INDICATOR_PROCESS_PREFIX}${i}`) ?? await convertHtmlToImageJpeg(processSection, 2);
                if (dataUrl) {
                  const imgProps = pdf.getImageProperties(dataUrl);
                  const pageWidth = pdf.internal.pageSize.getWidth() - 20;
                  const pageHeight = pdf.internal.pageSize.getHeight() - (currentY + 12) - 10;
                  const finalScale = calculateScaleToFitPage(imgProps.width, imgProps.height, pageWidth, pageHeight);
                  const finalImgWidth = imgProps.width * finalScale;
                  const finalImgHeight = imgProps.height * finalScale;
                  const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
                  pdf.addImage(dataUrl, 'JPEG', xOffset, currentY + 12, finalImgWidth, finalImgHeight);
                }
              }
            }
          }

          // 更新による改善効果（費用）- 各工程を別ページに出力
          if (updateInvestmentProfitLossData) {
            const costProcessCount = updateInvestmentProfitLossData.improvementEffectCostByProcess?.length || 0;
            for (let i = 0; i < costProcessCount; i++) {
              const processSection = document.getElementById(`${UPDATE_IMPROVEMENT_COST_PROCESS_PREFIX}${i}`);
              if (processSection) {
                // 簡易モード時、最初のコンテンツのみP&Lページを再利用（空白ページ防止）
                if (canReusePLPage) {
                  canReusePLPage = false;
                } else {
                  pdf.addPage();
                  addHeader();
                }

                currentY = headerHeight + 12;
                pdf.setFontSize(14);
                const processName = updateInvestmentProfitLossData.improvementEffectCostByProcess[i]?.processName || i18n.t('features.InvestmentScreening.pdf.process.processNumber', { num: i + 1 });
                pdf.text(i18n.t('features.InvestmentScreening.pdf.process.updateImprovementEffectCost', { processName }), 14, currentY);
                pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8);

                const dataUrl = getCachedImage(`${UPDATE_IMPROVEMENT_COST_PROCESS_PREFIX}${i}`) ?? await convertHtmlToImageJpeg(processSection, 2);
                if (dataUrl) {
                  const imgProps = pdf.getImageProperties(dataUrl);
                  const pageWidth = pdf.internal.pageSize.getWidth() - 20;
                  const pageHeight = pdf.internal.pageSize.getHeight() - (currentY + 20) - 10;
                  const finalScale = calculateScaleToFitPage(imgProps.width, imgProps.height, pageWidth, pageHeight);
                  const finalImgWidth = imgProps.width * finalScale;
                  const finalImgHeight = imgProps.height * finalScale;
                  const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
                  pdf.addImage(dataUrl, 'JPEG', xOffset, currentY + 20, finalImgWidth, finalImgHeight);
                }
              }
            }
          }

        }

        // 全工程合計を別ページに出力
        const totalSection = document.getElementById(UPDATE_IMPROVEMENT_EFFECT_TOTAL_ID);
        if (totalSection) {
          pdf.addPage();
          addHeader();

          currentY = headerHeight + 12;
          pdf.setFontSize(14);
          pdf.text(i18n.t('features.InvestmentScreening.pdf.label.updateImprovementEffectTotal'), 14, currentY);
          pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8);

          const dataUrl = getCachedImage(UPDATE_IMPROVEMENT_EFFECT_TOTAL_ID) ?? await convertHtmlToImageJpeg(totalSection, 2);
          if (dataUrl) {
            const imgProps = pdf.getImageProperties(dataUrl);
            const pageWidth = pdf.internal.pageSize.getWidth() - 20;
            const pageHeight = pdf.internal.pageSize.getHeight() - (currentY + 20) - 10;
            const finalScale = calculateScaleToFitPage(imgProps.width, imgProps.height, pageWidth, pageHeight);
            const finalImgWidth = imgProps.width * finalScale;
            const finalImgHeight = imgProps.height * finalScale;
            const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
            pdf.addImage(dataUrl, 'JPEG', xOffset, currentY + 20, finalImgWidth, finalImgHeight);
          }
        }
      }

      // 合理化投資の場合: 年度別損益（合理化前/後）と改善効果を出力
      if (projectData?.purpose === PurposeType.Rationalization.code && rationalizationInvestmentProfitLossData) {

        // 標準モードの場合は「年度別損益（合理化前/合理化後）」を表示しない
        if (!isSimpleMode) {

          // 年度別損益（合理化前）
          const beforeProfitLossSection = document.getElementById(RATIONALIZATION_BEFORE_PROFIT_LOSS_ID);
          if (beforeProfitLossSection) {
            pdf.addPage();
            addHeader();

            currentY = headerHeight + 12;
            pdf.setFontSize(14);
            pdf.text(i18n.t('features.InvestmentScreening.pdf.label.profitLossByYearRationalizationBefore'), 14, currentY);
            pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8);

            const dataUrl = getCachedImage(RATIONALIZATION_BEFORE_PROFIT_LOSS_ID) ?? await convertHtmlToImageJpeg(beforeProfitLossSection, 2);
            if (dataUrl) {
              const imgProps = pdf.getImageProperties(dataUrl);
              const pageWidth = pdf.internal.pageSize.getWidth() - 20;
              const pageHeight = pdf.internal.pageSize.getHeight() - (currentY + 20) - 10;
              const finalScale = calculateScaleToFitPage(imgProps.width, imgProps.height, pageWidth, pageHeight);
              const finalImgWidth = imgProps.width * finalScale;
              const finalImgHeight = imgProps.height * finalScale;
              const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
              pdf.addImage(dataUrl, 'JPEG', xOffset, currentY + 20, finalImgWidth, finalImgHeight);
            }
          }

          // 年度別損益（合理化後）
          const afterProfitLossSection = document.getElementById(RATIONALIZATION_AFTER_PROFIT_LOSS_ID);
          if (afterProfitLossSection) {
            pdf.addPage();
            addHeader();

            currentY = headerHeight + 12;
            pdf.setFontSize(14);
            pdf.text(i18n.t('features.InvestmentScreening.pdf.label.profitLossByYearRationalizationAfter'), 14, currentY);
            pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8);

            const dataUrl = getCachedImage(RATIONALIZATION_AFTER_PROFIT_LOSS_ID) ?? await convertHtmlToImageJpeg(afterProfitLossSection, 2);
            if (dataUrl) {
              const imgProps = pdf.getImageProperties(dataUrl);
              const pageWidth = pdf.internal.pageSize.getWidth() - 20;
              const pageHeight = pdf.internal.pageSize.getHeight() - (currentY + 20) - 10;
              const finalScale = calculateScaleToFitPage(imgProps.width, imgProps.height, pageWidth, pageHeight);
              const finalImgWidth = imgProps.width * finalScale;
              const finalImgHeight = imgProps.height * finalScale;
              const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
              pdf.addImage(dataUrl, 'JPEG', xOffset, currentY + 20, finalImgWidth, finalImgHeight);
            }
          }

        }

        // 合理化による改善効果（指標）- 各工程を別ページに出力
        const indicatorProcessCount = rationalizationInvestmentProfitLossData.improvementEffectIndicatorByProcess?.length || 0;
        for (let i = 0; i < indicatorProcessCount; i++) {
          const processSection = document.getElementById(`${RATIONALIZATION_IMPROVEMENT_INDICATOR_PROCESS_PREFIX}${i}`);
          if (processSection) {
            // 簡易モード時、最初のコンテンツのみP&Lページを再利用（空白ページ防止）
            if (canReusePLPage) {
              canReusePLPage = false;
            } else {
              pdf.addPage();
              addHeader();
            }

            currentY = headerHeight + 12;
            pdf.setFontSize(14);
            const processName = rationalizationInvestmentProfitLossData.improvementEffectIndicatorByProcess[i]?.processName || i18n.t('features.InvestmentScreening.pdf.process.processNumber', { num: i + 1 });
            pdf.text(i18n.t('features.InvestmentScreening.pdf.process.rationalizationImprovementEffectIndicator', { processName }), 14, currentY);

            const dataUrl = getCachedImage(`${RATIONALIZATION_IMPROVEMENT_INDICATOR_PROCESS_PREFIX}${i}`) ?? await convertHtmlToImageJpeg(processSection, 2);
            if (dataUrl) {
              const imgProps = pdf.getImageProperties(dataUrl);
              const pageWidth = pdf.internal.pageSize.getWidth() - 20;
              const pageHeight = pdf.internal.pageSize.getHeight() - (currentY + 12) - 10;
              const finalScale = calculateScaleToFitPage(imgProps.width, imgProps.height, pageWidth, pageHeight);
              const finalImgWidth = imgProps.width * finalScale;
              const finalImgHeight = imgProps.height * finalScale;
              const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
              pdf.addImage(dataUrl, 'JPEG', xOffset, currentY + 12, finalImgWidth, finalImgHeight);
            }
          }
        }

        // 合理化による改善効果（費用）- 各工程を別ページに出力
        const costProcessCount = rationalizationInvestmentProfitLossData.improvementEffectCostByProcess?.length || 0;
        for (let i = 0; i < costProcessCount; i++) {
          const processSection = document.getElementById(`${RATIONALIZATION_IMPROVEMENT_COST_PROCESS_PREFIX}${i}`);
          if (processSection) {
            // 簡易モード時、最初のコンテンツのみP&Lページを再利用（空白ページ防止）
            if (canReusePLPage) {
              canReusePLPage = false;
            } else {
              pdf.addPage();
              addHeader();
            }

            currentY = headerHeight + 12;
            pdf.setFontSize(14);
            const processName = rationalizationInvestmentProfitLossData.improvementEffectCostByProcess[i]?.processName || i18n.t('features.InvestmentScreening.pdf.process.processNumber', { num: i + 1 });
            pdf.text(i18n.t('features.InvestmentScreening.pdf.process.rationalizationImprovementEffectCost', { processName }), 14, currentY);
            pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8);

            const dataUrl = getCachedImage(`${RATIONALIZATION_IMPROVEMENT_COST_PROCESS_PREFIX}${i}`) ?? await convertHtmlToImageJpeg(processSection, 2);
            if (dataUrl) {
              const imgProps = pdf.getImageProperties(dataUrl);
              const pageWidth = pdf.internal.pageSize.getWidth() - 20;
              const pageHeight = pdf.internal.pageSize.getHeight() - (currentY + 20) - 10;
              const finalScale = calculateScaleToFitPage(imgProps.width, imgProps.height, pageWidth, pageHeight);
              const finalImgWidth = imgProps.width * finalScale;
              const finalImgHeight = imgProps.height * finalScale;
              const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
              pdf.addImage(dataUrl, 'JPEG', xOffset, currentY + 20, finalImgWidth, finalImgHeight);
            }
          }
        }

        // 改善効果の合計
        const improvementTotalSection = document.getElementById(RATIONALIZATION_IMPROVEMENT_EFFECT_TOTAL_ID);
        // 「合理化による改善効果（指標/費用）」のいずれかにデータが登録されている場合のみ「改善効果の合計」を表示
        if ((indicatorProcessCount || costProcessCount) && improvementTotalSection) {
          pdf.addPage();
          addHeader();

          currentY = headerHeight + 12;
          pdf.setFontSize(14);
          pdf.text(i18n.t('features.InvestmentScreening.pdf.label.improvementEffectTotal'), 14, currentY);
          pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8);

          const dataUrl = getCachedImage(RATIONALIZATION_IMPROVEMENT_EFFECT_TOTAL_ID) ?? await convertHtmlToImageJpeg(improvementTotalSection, 2);
          if (dataUrl) {
            const imgProps = pdf.getImageProperties(dataUrl);
            const pageWidth = pdf.internal.pageSize.getWidth() - 20;
            const pageHeight = pdf.internal.pageSize.getHeight() - (currentY + 20) - 10;
            const finalScale = calculateScaleToFitPage(imgProps.width, imgProps.height, pageWidth, pageHeight);
            const finalImgWidth = imgProps.width * finalScale;
            const finalImgHeight = imgProps.height * finalScale;
            const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
            pdf.addImage(dataUrl, 'JPEG', xOffset, currentY + 20, finalImgWidth, finalImgHeight);
          }
        }
      }

      // 回収期間（年度別データがある場合のみ）
      if (maxYears.length !== 0 && totalYearlyData) {
        pdf.addPage();
        addHeader();

        pdf.setFontSize(14);
        pdf.text(i18n.t('features.InvestmentScreening.pdf.label.paybackPeriod'), 14, currentY);
        pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8); // 通貨単位を表示
        const profitAndLossPaybackPeriodSection = document.getElementById(`${PROFIT_AND_LOSS_PAYBACK_PERIOD_TABLE_PREFIX}`);
        if (profitAndLossPaybackPeriodSection) {
          profitAndLossPaybackPeriodSection.style.overflowX = 'visible';
          profitAndLossPaybackPeriodSection.style.maxWidth = 'none';
          const paybackImagePos = await addTableAsImage(pdf, profitAndLossPaybackPeriodSection, currentY + 12);
          if (paybackImagePos !== undefined) {
            currentY = paybackImagePos;
          }
          currentY += 10;

          pdf.setFontSize(10);
          pdf.text(i18n.t('features.InvestmentScreening.pdf.message.paybackPeriodFormula'), 14, currentY);

          pdf.setFontSize(8);
          currentY = headerHeight + 12;
        }

        // 増分キャッシュフロー
        pdf.addPage();
        addHeader();

        pdf.setFontSize(14);
        pdf.text(i18n.t('features.InvestmentScreening.pdf.label.incrementalCashFlow'), 14, currentY);
        pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8); // 通貨単位を表示
        const profitAndLossCollectSimSection = document.getElementById(`${PROFIT_AND_LOSS_COLLECT_SIMULATION_TABLE_PREFIX}`);
        if (profitAndLossCollectSimSection) {
          profitAndLossCollectSimSection.style.overflowX = 'visible';
          profitAndLossCollectSimSection.style.maxWidth = 'none';
          const imagePos = await addTableAsImage(pdf, profitAndLossCollectSimSection, currentY + 12);
          if (imagePos !== undefined) {
            currentY = imagePos;
          }
          currentY += 10;

          pdf.setFontSize(10);
          pdf.text(i18n.t('features.InvestmentScreening.pdf.message.breakEvenNote'), 14, currentY);

          pdf.setFontSize(8);
          currentY = headerHeight + 12;
        }

        // DCF
        pdf.addPage();
        addHeader();

        pdf.setFontSize(14);
        pdf.text(i18n.t('features.InvestmentScreening.pdf.label.dcfMethod'), 14, currentY);
        pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, currentY + 8); // 通貨単位を表示
        const profitAndLossDcfSection = document.getElementById(`${PROFIT_AND_LOSS_DCF_TABLE_PREFIX}`);
        if (profitAndLossDcfSection) {
          profitAndLossDcfSection.style.overflowX = 'visible';
          profitAndLossDcfSection.style.maxWidth = 'none';
          await addTableAsImage(pdf, profitAndLossDcfSection, currentY + 12);
        }
      }
    } // end of isProfitLossHiddenMode/else (P&Lセクション)



    /** ---------- スケジュール ---------- */
    pdf.addPage();
    addHeader();
    renderHeader(i18n.t('features.InvestmentScreening.ScheduleTable.title'));

    // スケジュール: Ganttコンテナ（id="schedule-gantt-container"）をキャプチャしてPDFに貼り付け
    // - 画面のGanttと同じ見た目をそのまま出力する
    // - キャプチャ前にコンテナ幅を固定して全期間を可視化する
    {
      const schReviewItems = ctx.project.reviewItems;

      // Ganttコンテナをキャプチャ
      // schedule-gantt-wrapper: bg="white" の外側ラッパー（白背景保証）
      // schedule-gantt-container: overflowX="auto" の内側コンテナ（overflow解除対象）
      const wrapperEl = document.getElementById('schedule-gantt-wrapper');
      const ganttEl = document.getElementById('schedule-gantt-container');
      if (wrapperEl && ganttEl) {
        // --- キャプチャ前準備 ---
        // (1) 外側コンテナ・内側コンテナの overflow を visible に変更
        const wrapperOrigOverflow = wrapperEl.style.overflow;
        const wrapperOrigOverflowX = wrapperEl.style.overflowX;
        const wrapperOrigWidth = wrapperEl.style.width;
        wrapperEl.style.overflow = 'visible';
        wrapperEl.style.overflowX = 'visible';

        const ganttOrigOverflow = ganttEl.style.overflow;
        const ganttOrigOverflowX = ganttEl.style.overflowX;
        ganttEl.style.overflow = 'visible';
        ganttEl.style.overflowX = 'visible';

        // (2) gantt-task-react 内部スクロールコンテナ（overflow:auto を持つ子要素）も visible に変更
        type OverflowSave = { el: HTMLElement; overflow: string; overflowX: string; width: string };
        const innerScrollEls: OverflowSave[] = [];
        ganttEl.querySelectorAll('*').forEach(function (child) {
          const el = child as HTMLElement;
          const cs = getComputedStyle(el);
          if (cs.overflow === 'auto' || cs.overflowX === 'auto' || cs.overflow === 'scroll' || cs.overflowX === 'scroll') {
            innerScrollEls.push({ el, overflow: el.style.overflow, overflowX: el.style.overflowX, width: el.style.width });
            el.style.overflow = 'visible';
            el.style.overflowX = 'visible';
          }
        });

        // (3) SVG の実幅でコンテナ幅を設定（スクロール解除後に scrollWidth が正確になる）
        const captureWidth = Math.max(1466, ganttEl.scrollWidth);
        wrapperEl.style.width = `${captureWidth}px`;
        ganttEl.style.width = `${captureWidth}px`;
        innerScrollEls.forEach(function (s) { s.el.style.width = `${captureWidth}px`; });

        // (4) html2canvas でキャプチャする
        // html-to-image（foreignObjectベース）はChakra UIの外部CSSクラスや SVG 背景を正しく反映しない。
        // html2canvas は Canvas API で直接描画するため、SVG 背景の黒化問題を回避できる。
        const ganttDataUrl = await convertHtmlToImageJpegUsingHtml2Canvas(wrapperEl, 1.5, captureWidth);

        // --- スタイル復元 ---
        wrapperEl.style.overflow = wrapperOrigOverflow;
        wrapperEl.style.overflowX = wrapperOrigOverflowX;
        wrapperEl.style.width = wrapperOrigWidth;
        ganttEl.style.overflow = ganttOrigOverflow;
        ganttEl.style.overflowX = ganttOrigOverflowX;
        ganttEl.style.width = '';
        innerScrollEls.forEach(function (s) {
          s.el.style.overflow = s.overflow;
          s.el.style.overflowX = s.overflowX;
          s.el.style.width = s.width;
        });

        if (ganttDataUrl) {
          const imgProps = pdf.getImageProperties(ganttDataUrl);
          const pdfPageWidth = pdf.internal.pageSize.getWidth() - 20;
          const pdfPageHeight = pdf.internal.pageSize.getHeight() - headerHeight - 20;
          // ページ高さを超える場合はページに収まるようにスケーリング
          const scale = calculateScaleToFitPage(imgProps.width, imgProps.height, pdfPageWidth, pdfPageHeight);
          const imgWidth = imgProps.width * scale;
          const imgHeight = imgProps.height * scale;
          pdf.addImage(ganttDataUrl, 'JPEG', 10, headerHeight + 10, imgWidth, imgHeight);
        }
      }

      // 振り返りスケジュール: Ganttに含まれないため引き続き autoTable で出力
      if (schReviewItems && schReviewItems.length > 0) {
        // Ganttキャプチャ後に改ページしてから振り返りスケジュールを出力
        pdf.addPage();
        addHeader();
        const titleY = headerHeight + 14;
        pdf.setFont('NotoSansJP', 'bold');
        pdf.setFontSize(10);
        pdf.text(i18n.t('features.InvestmentScreening.ScheduleTable.reviewSchedule'), 10, titleY);
        autoTable(pdf, {
          head: [[
            { content: i18n.t('features.InvestmentScreening.ScheduleTable.reviewPhase'), styles: { halign: 'center', fontStyle: 'bold', fontSize: 7 } },
            { content: i18n.t('features.InvestmentScreening.ScheduleTable.reviewDate'), styles: { halign: 'center', fontStyle: 'bold', fontSize: 7 } },
          ]],
          body: schReviewItems.map(ri => {
            const phaseLabel = ReviewPhasePresets.find(p => p.value === ri.phase)?.label ?? ri.phase ?? '';
            return [phaseLabel, ri.date ?? ''];
          }),
          startY: titleY + 4,
          margin: { top: headerHeight + 10, left: 10, right: 10 },
          tableWidth: 100,
          styles: {
            font: 'NotoSansJP', fontStyle: 'normal', fontSize: 7,
            cellPadding: { top: 1, bottom: 1, left: 2, right: 2 },
            minCellHeight: 5,
          },
          headStyles: {
            fillColor: [230, 230, 230], textColor: [0, 0, 0],
            fontStyle: 'bold',
          },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 40 },
          },
          didDrawPage: () => { addHeader(); },
        });
      }
    }

    /** ---------- 設備投資 ---------- */
    pdf.addPage();
    addHeader();
    renderHeader(i18n.t('features.InvestmentScreening.pdf.title.equipment'));

    // 設備投資セクション（事前キャプチャ済み）
    const reactflowDataUrl = getCachedImage('reactflow');
    if (reactflowDataUrl) {
      const imgProps = pdf.getImageProperties(reactflowDataUrl);
      const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
      const pdfHeight = pdf.internal.pageSize.getHeight() - (headerHeight + 12) - 20;
      let imgWidth = pdfWidth;
      let imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
      if (imgHeight > pdfHeight) {
        imgHeight = pdfHeight;
        imgWidth = (imgProps.width * pdfHeight) / imgProps.height;
      }
      const xOffset = (pdf.internal.pageSize.getWidth() - imgWidth) / 2;
      pdf.addImage(reactflowDataUrl, 'PNG', xOffset, headerHeight + 12, imgWidth, imgHeight);
    }

    // 画像追加処理
    const addLineImagesToPDF = async (pdf: jsPDF, base64Array: string[], title: string) => {

      if (base64Array.length === 0) return;

      // サイズ調整用dpi
      const dpi = 350;

      // 最初のページを追加
      pdf.addPage();
      addHeader();

      finalY = headerHeight + 12;

      // タイトル
      pdf.setFontSize(14);
      pdf.text(title, 14, finalY);
      finalY += 8;

      const y = finalY;

      // 画像ファイル
      for (const base64 of base64Array) {
        const imgProps = pdf.getImageProperties(base64);
        const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
        const pdfHeight = pdf.internal.pageSize.getHeight() - y - 20;

        // サイズを調整
        let imgWidth = imgProps.width / dpi * 72;
        let imgHeight = imgProps.height / dpi * 72;

        // イメージサイズが用紙を超える場合
        if (imgWidth > pdfWidth) {
          imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
          imgWidth = pdfWidth;
        }
        if (imgHeight > pdfHeight) {
          // リサイズ
          imgHeight = pdfHeight;
          imgWidth = (imgProps.width * pdfHeight) / imgProps.height;
        }

        // 画像が余白に収まるか確認し、収まらない場合は改ページする
        if (finalY + imgHeight > pdf.internal.pageSize.getHeight() - 20) {
          pdf.addPage();
          addHeader();
          finalY = headerHeight + 12;
        }
        // 画像を中央に配置するためのX座標を計算
        const xOffset = (pdf.internal.pageSize.getWidth() - imgWidth) / 2;
        pdf.addImage(base64, 'PNG', xOffset, finalY, imgWidth, imgHeight);
        finalY += (imgHeight + 8);
      }
    };
    await addLineImagesToPDF(pdf, prefetchedProductionProcessImages, "ライン画像");

    /** 設備投資一覧
     * ※ autoTable方式を使用（html-to-image方式ではない）
     * 理由: 設備件数が多い場合にページ跨ぎが必要となるため、autoTableの自動改ページ機能を利用
     * 注意: NotoSansJP-Boldフォントは未登録のため、headStylesのfontStyleは'normal'を指定すること（'bold'にすると文字化けする）
     */
    const renderEquipmentTable = (pdf: jsPDFCustom, _startY: number) => {
      const validEquipmentAmounts = convEquipmentAmounts.filter((item): item is { amount: string } => item.amount !== undefined);
      const totalAmount = calculateTotalAmount(validEquipmentAmounts);

      const getSupplierName = (equipmentId: string) => {
        const supplier = supplierData.find(s => s.equipmentId !== null && s.equipmentId !== undefined && s.equipmentId === equipmentId);
        return supplier ? getMultilingualDisplayName(supplier.equipmentSupplierName) : '-';
      };

      // seq降順 → displayOrder昇順で並び替え
      const sortedEquipmentAmounts = [...convEquipmentAmounts].sort((a, b) => {
        const aSeq = processSeqMap[a.processId || ''] ?? 9999;
        const bSeq = processSeqMap[b.processId || ''] ?? 9999;
        if (aSeq !== bSeq) return bSeq - aSeq;
        // 同一工程内はdisplayOrder昇順（nullは最後）
        const orderA = a.displayOrder ?? Infinity;
        const orderB = b.displayOrder ?? Infinity;
        return orderA - orderB;
      });

      // 固定選択肢のコードと多言語キーの対応
      const QUANTITY_UNIT_KEYS = [
        { code: "unit", key: "features.InvestmentScreening.Equipment.options.quantityUnit.unit" },
        { code: "set",  key: "features.InvestmentScreening.Equipment.options.quantityUnit.set" },
        { code: "pcs",  key: "features.InvestmentScreening.Equipment.options.quantityUnit.pcs" },
      ] as const;

      const toSpreadsheetUnitLabel = (code?: string | null): string => {
        const targetCode = code || "unit"; // 初期値
        const matched = QUANTITY_UNIT_KEYS.find(item => item.code === targetCode);
        // 固定選択肢に一致すれば翻訳テキスト（例: "台"）、なければ自由入力値をそのまま返す
        return matched ? i18n.t(matched.key) : targetCode;
      };

      const equipmentData = sortedEquipmentAmounts.map((equipment) => {
        const amount = new Decimal(equipment.amount || 0);
        return [
          processItemNameMap[equipment.processId || ''] || '-',
          processItemNoMap[equipment.processId || ''] || '-',  // ItemNo
          equipment.equipmentName,
          equipment.quantity || '1',
          toSpreadsheetUnitLabel(equipment.quantityUnit),
          formatNumber(amount, "WithDecimal"),
          getSupplierName(equipment.equipmentId !== null && equipment.equipmentId !== undefined ? equipment.equipmentId : "")
        ]
      });

      const header = [
        i18n.t('features.InvestmentScreening.pdf.label.processName'),
        i18n.t('features.InvestmentScreening.pdf.label.itemNo'),
        i18n.t('features.InvestmentScreening.pdf.label.equipmentName'),
        i18n.t('features.InvestmentScreening.pdf.label.quantity'),
        i18n.t('features.InvestmentScreening.pdf.label.quantityUnit'),
        i18n.t('features.InvestmentScreening.pdf.label.requestedAmountShort'),
        i18n.t('features.InvestmentScreening.pdf.label.orderTo'),
      ];
      const body = [...equipmentData, ["合計", "", "", "", "", formatNumber(totalAmount, "WithDecimal"), ""]] as RowInput[];

      // 最初のページを追加
      pdf.addPage();

      pdf.setFontSize(12);
      autoTable(pdf, {
        head: [header],
        body: body,
        startY: headerHeight + 24,
        theme: 'striped',
        showHead: 'everyPage',
        margin: { top: headerHeight + 24 },
        styles: {
          font: "NotoSansJP",
          fontStyle: "normal",
          fontSize: 12
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'normal',
          halign: 'center'
        },
        bodyStyles: {
          textColor: [0, 0, 0],
          halign: 'right'
        },
        alternateRowStyles: {
          fillColor: [240, 240, 240],
        },
        columnStyles: {
          0: { halign: 'left' }, // 工程名を左寄せに設定
          1: { halign: 'left', cellWidth: 50 }, // ItemNoを左寄せに設定
          2: { halign: 'left', cellWidth: 45 }, // 設備名を左寄せに設定
          4: { halign: 'left' }, // 数量単位を左寄せに設定
          5: { halign: 'right' }, // 申請金額を右寄せに設定
          6: { halign: 'left' }, // 発注先を左寄せに設定
        },
        didDrawPage: (_data: any) => {
          // 全ページでヘッダーとタイトルを描画
          addHeader();
          pdf.setFontSize(14);
          pdf.text(i18n.t('features.InvestmentScreening.pdf.section.equipmentList'), 14, headerHeight + 12);
          pdf.text(`unit: ${i18n.t('common.label.thousandSuffix')}${currencyName}`, 14, headerHeight + 20);
        }
      });
    };
    renderEquipmentTable(pdf, headerHeight + 20);


    /** 設備に必要な人員数
     * ※ autoTable方式を使用（html-to-image方式ではない）
     * 理由: 設備件数が多い場合にページ跨ぎが必要となるため、autoTableの自動改ページ機能を利用
     * 注意: NotoSansJP-Boldフォントは未登録のため、headStylesのfontStyleは'normal'を指定すること（'bold'にすると文字化けする）
     */
    const generateEquipmentPersonnelTable = (pdf: jsPDFCustom, _startY: number) => {
      const unskilledWorker = equipmentAmounts.filter((item): item is { unskilledWorker: number } => item.amount !== undefined);         //単純工
      const multiSkilledWorker = equipmentAmounts.filter((item): item is { multiSkilledWorker: number } => item.amount !== undefined);   //多能工
      const technician = equipmentAmounts.filter((item): item is { technician: number } => item.amount !== undefined);                   //テクニシャン

      const SimpleLabor = totalNumberOfSimpleLabor(unskilledWorker);                         //単純工 合計人数
      const MultiSkilledWorkerse = totalNumberOfMultiSkilledWorkerse(multiSkilledWorker);    //多能工 合計人数
      const Technicians = totalNumberOfTechnicians(technician);                              //テクニシャン 合計人数

      // seq降順 → displayOrder昇順で並び替え
      const sortedEquipmentAmounts = [...convEquipmentAmounts].sort((a, b) => {
        const aSeq = processSeqMap[a.processId || ''] ?? 9999;
        const bSeq = processSeqMap[b.processId || ''] ?? 9999;
        if (aSeq !== bSeq) return bSeq - aSeq;
        // 同一工程内はdisplayOrder昇順（nullは最後）
        const orderA = a.displayOrder ?? Infinity;
        const orderB = b.displayOrder ?? Infinity;
        return orderA - orderB;
      });

      const equipmentData = sortedEquipmentAmounts.map((equipment) => {
        return [
          processItemNameMap[equipment.processId || ''] || '-',  //工程名
          processItemNoMap[equipment.processId || ''] || '-',    //ItemNo
          equipment.equipmentName,                        //設備名
          equipment.unskilledWorker,                      //単純工
          equipment.multiSkilledWorker,                   //多能工
          equipment.technician,                           //テクニシャン
          equipment.numberOfPeopleRequiredForEquipment    //合計人数（単純工 + 多能工 + テクニシャン）
        ]
      });

      const header = [
        i18n.t('features.InvestmentScreening.pdf.label.processName'),
        i18n.t('features.InvestmentScreening.pdf.label.itemNo'),
        i18n.t('features.InvestmentScreening.pdf.label.equipmentName'),
        i18n.t('features.InvestmentScreening.pdf.label.unskilledWorkerShift'),
        i18n.t('features.InvestmentScreening.pdf.label.multiSkilledWorkerShift'),
        i18n.t('features.InvestmentScreening.pdf.label.technicianShift'),
        i18n.t('features.InvestmentScreening.pdf.label.totalShift'),
      ];

      const body = [...equipmentData, ["総合計", "", "", SimpleLabor, MultiSkilledWorkerse, Technicians, SimpleLabor + MultiSkilledWorkerse + Technicians]] as RowInput[];

      // 最初のページを追加
      pdf.addPage();

      pdf.setFontSize(12);
      autoTable(pdf, {
        head: [header],
        body: body,
        startY: headerHeight + 28,
        theme: 'striped',
        showHead: 'everyPage',
        margin: { top: headerHeight + 28 },
        styles: {
          font: "NotoSansJP",
          fontStyle: "normal",
          fontSize: 12
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'normal',
          halign: 'center'
        },
        bodyStyles: {
          textColor: [0, 0, 0],
          halign: 'right'
        },
        alternateRowStyles: {
          fillColor: [240, 240, 240],
        },
        columnStyles: {
          0: { halign: 'left', cellWidth: 40 },
          1: { halign: 'left', cellWidth: 40 },
          2: { halign: 'left', cellWidth: 45 },
          3: { cellWidth: 35 },
          4: { cellWidth: 35 },
          5: { cellWidth: 35 },
          6: { cellWidth: 35 },
        },
        didDrawPage: (_data: any) => {
          // 全ページでヘッダーとタイトルを描画
          addHeader();
          pdf.setFontSize(14);
          pdf.text(i18n.t('features.InvestmentScreening.pdf.section.equipmentPersonnel'), 14, headerHeight + 12);
          pdf.text(i18n.t('features.InvestmentScreening.pdf.message.personnelSummary', { simple: SimpleLabor, multi: MultiSkilledWorkerse, technician: Technicians, total: SimpleLabor + MultiSkilledWorkerse + Technicians }), 14, headerHeight + 20);
        }
      });
    };
    generateEquipmentPersonnelTable(pdf, headerHeight + 20);


    /**------------- ベンチマーク -------------- */
    // 複数の ref を受け取り、設備ごとに PDF に描画
    const renderBenchmarkAsImage = async (
      pdf: jsPDF,
      benchmarkRefsArg: React.RefObject<Array<HTMLDivElement | null>>,
      headerHeightArg: number
    ) => {
      //PDFのラベル表示
      const renderTableHeader = () => {
        pdf.setFontSize(14);
        pdf.text(i18n.t('features.InvestmentScreening.pdf.section.benchmarkList'), 14, headerHeightArg);

      };
      for (let i = 0; i < benchmarkRefsArg.current!.length; i++) {
        const ref = benchmarkRefsArg.current![i];
        if (!ref) continue;
        console.log("start:", `${ref}${i}`)

        try {
          // HTML を JPEG に変換（事前キャプチャ済み画像を使用）
          const dataUrl = getCachedImage(`benchmark-${i}`) ?? await convertHtmlToImageJpeg(ref, 2);
          if (!dataUrl) continue;

          const imgProps = pdf.getImageProperties(dataUrl);
          const originalImgWidth = imgProps.width;
          const originalImgHeight = imgProps.height;

          const pageWidth = pdf.internal.pageSize.getWidth() - 20;  // 左右マージン
          const pageHeight = pdf.internal.pageSize.getHeight() - 30; // 上下マージン

          const finalScale = calculateScaleToFitPage(originalImgWidth, originalImgHeight, pageWidth, pageHeight);
          const finalImgWidth = originalImgWidth * finalScale;
          const finalImgHeight = originalImgHeight * finalScale;

          const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;

          pdf.addPage();
          addHeader();
          renderTableHeader();
          pdf.setFontSize(12);

          pdf.addImage(dataUrl, 'JPEG', xOffset, headerHeightArg + 5, finalImgWidth, finalImgHeight);
        } catch (error) {
          console.error(`Error rendering benchmark ${i}:`, error);
        }
      }
    };
    await renderBenchmarkAsImage(pdf, refs.benchmarkRefs, headerHeight + 8);

    /**------------- アップロード済みファイル リンク表示 -------------- */
    const renderUploadedFiles = async (pdf: jsPDFCustom, files: any, title?: string) => {
      if (!files) return;

      finalY = headerHeight + 12;

      // タイトル
      if (title) {
        // 最初のページを追加
        pdf.addPage();
        addHeader();
        pdf.setFontSize(14);
        pdf.text(title, 14, finalY);
        finalY += 14;
      }

      // ファイル一覧
      files.forEach((file: any) => {
        pdf.setFontSize(12);
        // マルウェア検知ファイルはリンクを張らず、ファイル名と注記のみ記載する
        const downloadable = !!file.url && !isScanBlocked(file.scanStatus);
        const label = downloadable
          ? file.fileName
          : `${file.fileName}（${i18n.t('components.ScanStatusBadge.label.threat')}）`;
        if (downloadable) {
          pdf.setTextColor(0, 0, 255);
          pdf.textWithLink(label, 14, finalY, { url: file.url || "" });
          const textWidth = pdf.getTextWidth(label);
          pdf.setLineWidth(0.5);
          pdf.line(14, finalY + 1, 14 + textWidth, finalY + 1);
        } else {
          pdf.setTextColor(128, 128, 128);
          pdf.text(label, 14, finalY);
        }
        pdf.setTextColor(0, 0, 0);
        // 次の行が余白を超えるか確認し、超える場合は改ページする
        if (finalY + 8 > pdf.internal.pageSize.getHeight() - 30) {
          pdf.addPage();
          addHeader();
          finalY = headerHeight + 12; // 描画位置をリセット
        } else {
          finalY += 8;
        }
      });
    };
    const equipmentFiles = uploadedFilesByFolder.find(f => f.folderName === "EquipmentFiles")?.files;
    await renderUploadedFiles(pdf, equipmentFiles, i18n.t('features.InvestmentScreening.pdf.section.equipmentDocs'));

    await addLineImagesToPDF(pdf, prefetchedEquipmentImages, "設備画像");

    /** ---------- 更新前後サマリーデータ一覧（更新投資の場合のみ、プロセスごとにページ分割） ---------- */
    if (projectData?.purpose === PurposeType.UpdateAndOther.code && updateSummaryData.length > 0) {
      // フィルタリング済みの更新前後サマリーデータを取得（HTMLと同じロジック）
      const existingProcessIds = new Set(equipmentAmounts.map(e => e.processId).filter(Boolean));
      const filteredUpdateSummaryData = updateSummaryData.filter((summary: UpdateSummaryInputData) => {
        return existingProcessIds.has(summary.productionLineId || "");
      });

      // プロセスごとにページ分割して出力
      for (let i = 0; i < filteredUpdateSummaryData.length; i++) {
        const updateSummaryItemSection = document.getElementById(`${UPDATE_SUMMARY_ITEM_PREFIX}${i}`);
        if (updateSummaryItemSection) {
          pdf.addPage();
          addHeader();

          pdf.setFontSize(14);
          const processName = filteredUpdateSummaryData[i].productionLineName || i18n.t('features.InvestmentScreening.pdf.process.processNumber', { num: i + 1 });
          pdf.text(i18n.t('features.InvestmentScreening.pdf.process.updateSummaryList', { processName }), 14, headerHeight + 12);

          updateSummaryItemSection.style.overflowX = 'visible';
          updateSummaryItemSection.style.maxWidth = 'none';
          updateSummaryItemSection.style.overflow = 'visible';
          // ページに収まるようにスケーリング（getBoundingClientRectを使用して正確な高さを取得）
          const rect = updateSummaryItemSection.getBoundingClientRect();
          const actualHeight = Math.max(updateSummaryItemSection.scrollHeight, updateSummaryItemSection.offsetHeight, rect.height);
          const dataUrl = await toJpeg(updateSummaryItemSection, {
            width: updateSummaryItemSection.scrollWidth,
            canvasWidth: updateSummaryItemSection.scrollWidth,
            height: actualHeight,
            canvasHeight: actualHeight,
            backgroundColor: '#FFFFFF',
            pixelRatio: 2,
            quality: 0.8
          });
          if (dataUrl) {
            const imgProps = pdf.getImageProperties(dataUrl);
            const pageWidth = pdf.internal.pageSize.getWidth() - 20;
            const pageHeight = pdf.internal.pageSize.getHeight() - (headerHeight + 20) - 10;
            const finalScale = calculateScaleToFitPage(imgProps.width, imgProps.height, pageWidth, pageHeight);
            const finalImgWidth = imgProps.width * finalScale;
            const finalImgHeight = imgProps.height * finalScale;
            const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
            pdf.addImage(dataUrl, 'JPEG', xOffset, headerHeight + 20, finalImgWidth, finalImgHeight);
          }
        }
      }
    }


    /** ---------- 費用 ---------- */
    pdf.addPage();
    addHeader();
    renderHeader(i18n.t('features.InvestmentScreening.pdf.title.cost'));

    const fileLiset = uploadedFilesByFolder ?? [];
    const hasProfitAndLossFiles = (fileLiset.find(f => f.folderName === "ProfitAndLoss")?.files?.length ?? 0) > 0;
    const hasReportFiles = (fileLiset.find(f => f.folderName === "Report")?.files?.length ?? 0) > 0;
    const hasNoFiles = !hasProfitAndLossFiles && !hasReportFiles; 

    if (hasNoFiles) {
      pdf.setFontSize(12);
      pdf.setTextColor(102, 102, 102);
      pdf.text(i18n.t('features.InvestmentScreening.pdf.message.noAttachedFile'), 14, headerHeight + 12);
      pdf.setTextColor(0, 0, 0);
    }

    if (isSimpleMode) {
      // 簡易モード時は見出しとメッセージのみ表示
      pdf.setFontSize(12);
      pdf.setTextColor(102, 102, 102);
      pdf.text(i18n.t('features.InvestmentScreening.pdf.message.simpleModeExcluded'), 14, headerHeight + 12);
      pdf.setTextColor(0, 0, 0);
    } else {

      const profitAndLossFiles = uploadedFilesByFolder.find(f => f.folderName === "ProfitAndLoss")?.files;
      const reportFiles = uploadedFilesByFolder.find(f => f.folderName === "Report")?.files;

      // 費用セクション内のファイルリンク表示（同一ページ内、収まらない場合のみ改ページ）
      const renderCostFiles = (files: any[] | undefined, subTitle: string) => {
        if (!files || files.length === 0) return;

        // サブタイトル表示前に改ページ判定
        if (finalY + 14 > pdf.internal.pageSize.getHeight() - 30) {
          pdf.addPage();
          addHeader();
          finalY = headerHeight + 12;
        }

        // サブタイトル
        pdf.setFontSize(14);
        pdf.setTextColor(0, 0, 0);
        pdf.text(subTitle, 14, finalY);
        finalY += 10;

        // ファイル一覧
        files.forEach((file: any) => {
          pdf.setFontSize(12);
          // マルウェア検知ファイルはリンクを張らず、ファイル名と注記のみ記載する
          const downloadable = !!file.url && !isScanBlocked(file.scanStatus);
          const label = downloadable
            ? file.fileName
            : `${file.fileName}（${i18n.t('components.ScanStatusBadge.label.threat')}）`;
          if (downloadable) {
            pdf.setTextColor(0, 0, 255);
            pdf.textWithLink(label, 14, finalY, { url: file.url || "" });
            const textWidth = pdf.getTextWidth(label);
            pdf.setLineWidth(0.5);
            pdf.line(14, finalY + 1, 14 + textWidth, finalY + 1);
          } else {
            pdf.setTextColor(128, 128, 128);
            pdf.text(label, 14, finalY);
          }
          pdf.setTextColor(0, 0, 0);

          // 次の行が余白を超えるか確認し、超える場合は改ページする
          if (finalY + 8 > pdf.internal.pageSize.getHeight() - 30) {
            pdf.addPage();
            addHeader();
            finalY = headerHeight + 12;
          } else {
            finalY += 8;
          }
        });

        finalY += 6; // サブセクション間のスペース
      };

      finalY = headerHeight + 12;
      renderCostFiles(profitAndLossFiles, i18n.t('features.InvestmentScreening.pdf.section.plsFiles'));
      renderCostFiles(reportFiles, i18n.t('features.InvestmentScreening.pdf.section.reportFiles'));
    } // end of isSimpleMode/else (費用セクション)

    /** ---------- Simulation ---------- */
    pdf.addPage();
    addHeader();
    renderHeader("Simulation");

    if (isProfitLossHiddenMode) {
      // 損益非表示モード時は見出しとメッセージのみ表示
      pdf.setFontSize(12);
      pdf.setTextColor(102, 102, 102);
      pdf.text(i18n.t('common.message.profitLossHiddenSectionNote'), 14, headerHeight + 12);
      pdf.setTextColor(0, 0, 0);
    } else {
      // 簡易モード時のみ、最初のSimulation配下コンテンツでSimulationページを再利用するためのフラグ
      let canReuseSimPage = isSimpleMode;

      // Simulation（事前キャプチャ済み画像を使用）
      const addSimulationCachedToPDF = (pdf: jsPDFCustom, cacheKey: string, startY: number) => {
        const dataUrl = getCachedImage(cacheKey);
        if (!dataUrl) return;
        const imgProps = pdf.getImageProperties(dataUrl);
        const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
        const pdfHeight = pdf.internal.pageSize.getHeight() - startY - 10;
        let imgWidth = pdfWidth;
        let imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
        if (imgHeight > pdfHeight) {
          imgHeight = pdfHeight;
          imgWidth = (imgProps.width * pdfHeight) / imgProps.height;
        }
        const xOffset = (pdf.internal.pageSize.getWidth() - imgWidth) / 2;
        pdf.addImage(dataUrl, 'JPEG', xOffset, startY, imgWidth, imgHeight);
      };
      addSimulationCachedToPDF(pdf, 'simulation-0', headerHeight + 2);

      // 簡易モード時は simulation-0 が空のため、Simulationページを再利用
      if (canReuseSimPage) {
        canReuseSimPage = false;
      } else {
        pdf.addPage();
        addHeader();
      }
      addSimulationCachedToPDF(pdf, 'simulation-1', headerHeight + 2);

      // 更新投資または合理化投資でプロセス別チャートがある場合はsimulationRef3をスキップ（個別にページ分割で出力）
      if (!((projectData?.purpose === PurposeType.UpdateAndOther.code || projectData?.purpose === PurposeType.Rationalization.code) && productionCapacityProcessChartDataList && productionCapacityProcessChartDataList.length > 0) &&
        !(projectData?.purpose === PurposeType.Rationalization.code && rationalizationSummaryChartDataList && rationalizationSummaryChartDataList.length > 0)) {
        pdf.addPage();
        addHeader();
        addSimulationCachedToPDF(pdf, 'simulation-2', headerHeight + 2);
      }


      // 生産能力（プロセス）のプロセスごとページ分割（更新投資または合理化投資の場合）
      if ((projectData?.purpose === PurposeType.UpdateAndOther.code || projectData?.purpose === PurposeType.Rationalization.code) && productionCapacityProcessChartDataList && productionCapacityProcessChartDataList.length > 0) {
        for (let i = 0; i < productionCapacityProcessChartDataList.length; i++) {
          const processItemSection = document.getElementById(`${PRODUCTION_CAPACITY_PROCESS_ITEM_PREFIX}${i}`);
          if (processItemSection) {
            pdf.addPage();
            addHeader();

            const dataUrl = getCachedImage(`${PRODUCTION_CAPACITY_PROCESS_ITEM_PREFIX}${i}`) ?? await convertHtmlToImageJpeg(processItemSection, 2);
            if (dataUrl) {
              const imgProps = pdf.getImageProperties(dataUrl);
              const pageWidth = pdf.internal.pageSize.getWidth() - 20;
              const pageHeight = pdf.internal.pageSize.getHeight() - (headerHeight + 2) - 10;
              const finalScale = calculateScaleToFitPage(imgProps.width, imgProps.height, pageWidth, pageHeight);
              const finalImgWidth = imgProps.width * finalScale;
              const finalImgHeight = imgProps.height * finalScale;
              const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
              pdf.addImage(dataUrl, 'JPEG', xOffset, headerHeight + 2, finalImgWidth, finalImgHeight);
            }
          }
        }
      }

      // 合理化前後サマリーチャートのプロセスごとページ分割（合理化投資の場合のみ）
      // 指標・費用それぞれ RATIONALIZATION_SUMMARY_CHARTS_PER_PAGE 個を超える場合は改ページする
      if (projectData?.purpose === PurposeType.Rationalization.code && rationalizationSummaryChartDataList && rationalizationSummaryChartDataList.length > 0) {

        /**
         * プロセス全体のセクション(chartItemSection)上で、
         * 不要な部分をdisplay:noneにしてキャプチャ→PDFページに出力するヘルパー関数
         */
        const captureAndAddPage = async (element: HTMLElement) => {
          // キャプチャ前に下部に余白を追加（チャートのX軸ラベルが見切れないようにする）
          const originalPaddingBottom = element.style.paddingBottom;
          element.style.paddingBottom = '16px';

          const dataUrl = await convertHtmlToImageJpeg(element, 2);

          // 余白を元に戻す
          element.style.paddingBottom = originalPaddingBottom;

          if (dataUrl) {
            pdf.addPage();
            addHeader();
            const imgProps = pdf.getImageProperties(dataUrl);
            const pdfPageWidth = pdf.internal.pageSize.getWidth() - 20;
            const pdfPageHeight = pdf.internal.pageSize.getHeight() - (headerHeight + 2) - 10;
            const scale = calculateScaleToFitPage(imgProps.width, imgProps.height, pdfPageWidth, pdfPageHeight);
            const finalImgWidth = imgProps.width * scale;
            const finalImgHeight = imgProps.height * scale;
            const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;
            pdf.addImage(dataUrl, 'JPEG', xOffset, headerHeight + 2, finalImgWidth, finalImgHeight);
          }
        };

        /**
         * グループセクション内のチャートをページ分割してキャプチャする関数
         * chartItemSection: プロセス全体のコンテナ（見出し含む）
         * groupSectionId: 対象グループ（指標or費用）のセクションID
         * otherGroupSectionId: もう一方のグループのセクションID（非表示にする）
         */
        const captureGroupInPages = async (
          chartItemSection: HTMLElement,
          groupSectionId: string,
          otherGroupSectionId: string,
        ) => {
          const groupSection = document.getElementById(groupSectionId);
          const otherGroupSection = document.getElementById(otherGroupSectionId);
          if (!groupSection) return;

          const chartElements = Array.from(groupSection.querySelectorAll<HTMLElement>('[data-chart-item-id]'));
          const totalCharts = chartElements.length;
          if (totalCharts === 0) return;

          const totalPages = Math.ceil(totalCharts / RATIONALIZATION_SUMMARY_CHARTS_PER_PAGE);

          // もう一方のグループセクションを非表示
          if (otherGroupSection) otherGroupSection.style.display = 'none';

          // グループラベルのHeading要素を取得（textContent書き換え用）
          const groupHeading = groupSection.querySelector('[data-group-label]') as HTMLElement | null;
          const originalHeadingText = groupHeading?.textContent || '';

          for (let page = 0; page < totalPages; page++) {
            const startIdx = page * RATIONALIZATION_SUMMARY_CHARTS_PER_PAGE;
            const endIdx = Math.min(startIdx + RATIONALIZATION_SUMMARY_CHARTS_PER_PAGE, totalCharts);

            // ページ番号をグループラベルに一時追加
            if (groupHeading && totalPages > 1) {
              groupHeading.textContent = `${originalHeadingText} (${page + 1}/${totalPages})`;
            }

            // 対象ページのチャートのみ表示、それ以外を非表示
            chartElements.forEach((el, idx) => {
              el.style.display = (idx >= startIdx && idx < endIdx) ? '' : 'none';
            });

            // プロセス全体のセクションをそのままキャプチャ（DOM移動なし）
            await captureAndAddPage(chartItemSection);
          }

          // 全チャートの表示を復元
          chartElements.forEach((el) => { el.style.display = ''; });
          // グループラベルを復元
          if (groupHeading) groupHeading.textContent = originalHeadingText;
          // もう一方のグループセクションの表示を復元
          if (otherGroupSection) otherGroupSection.style.display = '';
        };

        for (let i = 0; i < rationalizationSummaryChartDataList.length; i++) {
          const processChartData = rationalizationSummaryChartDataList[i];
          if (processChartData.items.length === 0) continue;

          const indicatorItems = processChartData.items.filter(item => item.group === "indicator");
          const costItems = processChartData.items.filter(item => item.group === "cost");
          const improvementEffectItems = processChartData.items.filter(item => item.group === "improvementEffect");

          const chartItemSection = document.getElementById(`${RATIONALIZATION_SUMMARY_CHART_ITEM_PREFIX}${i}`);
          if (!chartItemSection) continue;

          const indicatorSectionId = `${RATIONALIZATION_SUMMARY_CHART_ITEM_PREFIX}${i}-indicator`;
          const costSectionId = `${RATIONALIZATION_SUMMARY_CHART_ITEM_PREFIX}${i}-cost`;
          const improvementEffectSectionId = `${RATIONALIZATION_SUMMARY_CHART_ITEM_PREFIX}${i}-improvementEffect`;

          // 全セクションが上限以下の場合、プロセス全体を1ページに出力
          if (
            indicatorItems.length <= RATIONALIZATION_SUMMARY_CHARTS_PER_PAGE &&
            costItems.length <= RATIONALIZATION_SUMMARY_CHARTS_PER_PAGE &&
            improvementEffectItems.length <= RATIONALIZATION_SUMMARY_CHARTS_PER_PAGE
          ) {
            await captureAndAddPage(chartItemSection);
          } else {
            // 指標の出力
            if (indicatorItems.length > RATIONALIZATION_SUMMARY_CHARTS_PER_PAGE) {
              const improvementEffectSection = document.getElementById(improvementEffectSectionId);
              if (improvementEffectSection) improvementEffectSection.style.display = 'none';
              await captureGroupInPages(chartItemSection, indicatorSectionId, costSectionId);
              if (improvementEffectSection) improvementEffectSection.style.display = '';
            } else if (indicatorItems.length > 0) {
              // 指標は上限以下 → 費用・改善効果を非表示にしてプロセス全体をキャプチャ
              const costSection = document.getElementById(costSectionId);
              const improvementEffectSection = document.getElementById(improvementEffectSectionId);
              if (costSection) costSection.style.display = 'none';
              if (improvementEffectSection) improvementEffectSection.style.display = 'none';
              await captureAndAddPage(chartItemSection);
              if (costSection) costSection.style.display = '';
              if (improvementEffectSection) improvementEffectSection.style.display = '';
            }

            // 費用の出力
            if (costItems.length > RATIONALIZATION_SUMMARY_CHARTS_PER_PAGE) {
              const improvementEffectSection = document.getElementById(improvementEffectSectionId);
              if (improvementEffectSection) improvementEffectSection.style.display = 'none';
              await captureGroupInPages(chartItemSection, costSectionId, indicatorSectionId);
              if (improvementEffectSection) improvementEffectSection.style.display = '';
            } else if (costItems.length > 0) {
              // 費用は上限以下 → 指標・改善効果を非表示にしてプロセス全体をキャプチャ
              const indicatorSection = document.getElementById(indicatorSectionId);
              const improvementEffectSection = document.getElementById(improvementEffectSectionId);
              if (indicatorSection) indicatorSection.style.display = 'none';
              if (improvementEffectSection) improvementEffectSection.style.display = 'none';
              await captureAndAddPage(chartItemSection);
              if (indicatorSection) indicatorSection.style.display = '';
              if (improvementEffectSection) improvementEffectSection.style.display = '';
            }

            // 改善効果の出力（通常1アイテムのため上限超えは想定外だが念のため対応）
            if (improvementEffectItems.length > 0) {
              const indicatorSection = document.getElementById(indicatorSectionId);
              const costSection = document.getElementById(costSectionId);
              if (indicatorSection) indicatorSection.style.display = 'none';
              if (costSection) costSection.style.display = 'none';
              await captureAndAddPage(chartItemSection);
              if (indicatorSection) indicatorSection.style.display = '';
              if (costSection) costSection.style.display = '';
            }
          }
        }
      }
    } // end of isProfitLossHiddenMode/else (Simulationセクション)

    // 最終的な投資判断の理由
    pdf.addPage();
    addHeader();

    // PDFのラベル表示
    pdf.setFontSize(16);
    pdf.text(i18n.t('features.InvestmentScreening.pdf.label.finalInvestmentReason'), 14, headerHeight + 12);
    wrapText(`${projectData?.finalInvestmentReason ?? ""}`, Margin, 45, 14, 10);




    // 見積チェックリストの直前で呼び出し元のフックを実行する（振り返り3画面の挿入位置）
    if (hooks?.beforeEstimateCheckList) {
      await hooks.beforeEstimateCheckList(pdf);
    }

    /** ---------- 見積チェックリスト ---------- */
    pdf.addPage();
    addHeader();
    renderHeader(i18n.t('features.InvestmentScreening.pdf.title.estimateCheckList'));

    const renderEstimateCheckListAsImage = async (
      pdf: jsPDF,
      checkListRef: { current: HTMLDivElement | null },
      startY: number
    ) => {

      if (!checkListRef.current) {
        console.error("Estimate Check List ref is null or undefined.");
        return;
      }

      try {

        // ブラウザ幅に依存しないよう、要素幅を一時的に1466pxに設定
        const originalWidth = checkListRef.current.style.width;
        checkListRef.current.style.width = '1466px';
        // レイアウト再計算を待つ
        await new Promise<void>(resolve => requestAnimationFrame(() => setTimeout(resolve, 100)));

        const dataUrl = await convertHtmlToImageJpegUsingHtml2Canvas(checkListRef.current, 2, 1466);

        // 幅を復元
        checkListRef.current.style.width = originalWidth;

        if (!dataUrl) {
          console.error("Failed to generate image.");
          return;
        }

        const imgProps = pdf.getImageProperties(dataUrl);
        const originalImgWidth = imgProps.width;
        const originalImgHeight = imgProps.height;

        const pageWidth = pdf.internal.pageSize.getWidth() - 20;
        const pageHeight = pdf.internal.pageSize.getHeight() - startY - 20;

        const scale = Math.min(
          pageWidth / originalImgWidth,
          pageHeight / originalImgHeight
        );

        const finalImgWidth = originalImgWidth * scale;
        const finalImgHeight = originalImgHeight * scale;

        const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;

        pdf.addImage(dataUrl, "JPEG", xOffset, startY, finalImgWidth, finalImgHeight);

      } catch (error) {
        console.error("Error during image rendering:", error);
      }

    };

    const htmlDivRefs: { current: HTMLDivElement | null }[] = refs.estimateCheckListRefs.current!.map((item) => ({
      current: item,
    }));

    for (let i = 0; i < htmlDivRefs.length; i++) {

      const ref = htmlDivRefs[i];
      const isLastElement = i === htmlDivRefs.length - 1;

      await renderEstimateCheckListAsImage(pdf, ref, headerHeight + 8);

      if (!isLastElement) {
        pdf.addPage();
        addHeader();
      }

    }


    /** ---------- 10か条チェックリスト ---------- */
    pdf.addPage();
    addHeader();
    renderHeader(i18n.t('features.InvestmentScreening.pdf.title.tenPrincipleCheckList'));

    finalY = headerHeight + 12;

    const renderCheckListAsImage = async (pdf: jsPDF, checkListRef: { current: HTMLDivElement | null }, startY: number) => {
      if (!checkListRef.current) {
        console.error('Check List ref is null or undefined.');
        return;
      }

      try {
        // １０か条チェックリストの下部が切れてキャプチャされてしまうので、paddingBottomを設定
        const element = checkListRef.current;
        const cloned = element.cloneNode(true) as HTMLElement;

        cloned.style.paddingBottom = '20px';
        cloned.style.width = '1466px';
        document.body.appendChild(cloned);

        // キャプチャ
        const dataUrl = await convertHtmlToImageJpeg(cloned, 2);

        // 使い終わったら削除
        cloned.remove();

        if (!dataUrl) {
          console.error("Failed to generate image.");
          return;
        }

        const imgProps = pdf.getImageProperties(dataUrl);
        const originalImgWidth = imgProps.width;
        const originalImgHeight = imgProps.height;

        const pageWidth = pdf.internal.pageSize.getWidth() - 20; // consider left and right margins
        const pageHeight = pdf.internal.pageSize.getHeight() - 30; // consider top and bottom margins

        const finalScale = calculateScaleToFitPage(originalImgWidth, originalImgHeight, pageWidth, pageHeight);

        const finalImgWidth = originalImgWidth * finalScale;
        const finalImgHeight = originalImgHeight * finalScale;

        // 現在のページに収まらない場合のみ改ページする
        let drawY = startY;
        if (drawY + finalImgHeight > pdf.internal.pageSize.getHeight() - 20) {
          pdf.addPage();
          addHeader();
          drawY = headerHeight + 12;
        }

        const xOffset = (pdf.internal.pageSize.getWidth() - finalImgWidth) / 2;

        pdf.addImage(dataUrl, 'JPEG', xOffset, drawY, finalImgWidth, finalImgHeight);

        // 次のチェックリストの描画開始Y座標（余白込み）
        return drawY + finalImgHeight + 2;

      } catch (error) {
        console.error('Error capturing image:', error);
      }
    };

    const htmlDivRefsCheckList: { current: HTMLDivElement | null }[] = refs.checkListRefs.current!.map((item) => ({
      current: item,
    }));

    for (let i = 0; i < htmlDivRefsCheckList.length; i++) {

      const ref = htmlDivRefsCheckList[i];

      const nextY = await renderCheckListAsImage(pdf, ref, finalY);
      if (nextY !== undefined) {
        finalY = nextY;
      }

    }


    /** ---------- 添付資料 ---------- */
    pdf.addPage();
    addHeader();
    renderHeader(i18n.t('features.InvestmentScreening.pdf.title.attachments'));

    finalY = headerHeight + 12;
    const bomSnapshotFiles = uploadedFilesByFolder.find(f => f.folderName === "BomSnapshot")?.files;
    const othersFiles = uploadedFilesByFolder.find(f => f.folderName === "Others")?.files;

    // ファイルリンク描画ヘルパー（finalYを維持しながら描画）
    const renderFileLinksInline = (files: any[]) => {
      files.forEach((file: any) => {
        pdf.setFontSize(12);
        pdf.setTextColor(0, 0, 255);
        pdf.textWithLink(file.fileName, 14, finalY, { url: file.url || "" });
        const textWidth = pdf.getTextWidth(file.fileName);
        pdf.setLineWidth(0.5);
        pdf.line(14, finalY + 1, 14 + textWidth, finalY + 1);
        pdf.setTextColor(0, 0, 0);
        if (finalY + 8 > pdf.internal.pageSize.getHeight() - 30) {
          pdf.addPage();
          addHeader();
          finalY = headerHeight + 12;
        } else {
          finalY += 8;
        }
      });
    };

    if ((!bomSnapshotFiles || bomSnapshotFiles.length === 0) && (!othersFiles || othersFiles.length === 0)) {
      pdf.setFontSize(12);
      pdf.setTextColor(102, 102, 102);
      pdf.text(i18n.t('features.InvestmentScreening.pdf.message.noAttachedDocuments'), 14, headerHeight + 12);
      pdf.setTextColor(0, 0, 0);
    }

    // 申請時BOM情報（先頭に表示）
    if (bomSnapshotFiles && bomSnapshotFiles.length > 0) {
      pdf.setFontSize(13);
      pdf.text(i18n.t('features.InvestmentScreening.pdf.section.bomSnapshot'), 14, finalY);
      finalY += 10;
      renderFileLinksInline(bomSnapshotFiles);
      finalY += 4;
    }

    // その他添付ファイル
    if (othersFiles && othersFiles.length > 0) {
      if (bomSnapshotFiles && bomSnapshotFiles.length > 0) {
        // BOMファイルと区別するためサブタイトルを表示
        if (finalY + 16 > pdf.internal.pageSize.getHeight() - 30) {
          pdf.addPage();
          addHeader();
          finalY = headerHeight + 12;
        }
        pdf.setFontSize(13);
        pdf.text(i18n.t('features.InvestmentScreening.pdf.section.othersAttachment'), 14, finalY);
        finalY += 10;
      }
      renderFileLinksInline(othersFiles);
    }

    /** ---------- 確認者 ---------- */
    const confirmerListSection = document.getElementById(CONFIRMER_LIST_BOX_PREFIX);
    if (confirmerListSection) {
      pdf.addPage();
      addHeader();
      renderHeader(i18n.t('features.InvestmentScreening.pdf.title.confirmer'));
      currentY = headerHeight + 4;

      confirmerListSection.style.overflowX = 'visible';
      confirmerListSection.style.maxWidth = 'none';
      const confirmerImagePos = await addTableAsImage(pdf, confirmerListSection, currentY);
      if (confirmerImagePos !== undefined) {
        currentY = confirmerImagePos;
      }
      currentY += 14;
    }

    /** ---------- 承認履歴 ---------- */
    pdf.addPage();
    addHeader();
    renderHeader(i18n.t('features.InvestmentScreening.pdf.title.approvalHistory'));

    currentY = headerHeight + 4;

    const approvalListSection = document.getElementById(`${APPROVAL_LIST_BOX_PREFIX}`);
    if (approvalListSection) {
      approvalListSection.style.overflowX = 'visible';
      approvalListSection.style.maxWidth = 'none';
      const imagePos = await addTableAsImage(pdf, approvalListSection, currentY);
      if (imagePos !== undefined) {
        currentY = imagePos;
      }
      currentY += 14;
      addHeader();

      // 最後のみページ番号を付与
      pdf.setFontSize(10);
      const pageCount = pdf.internal.pages.length - 1;
      const pageNoPosition = pageWidth - 25;
      pdf.text(`Page ${pageCount}`, pageNoPosition, 205);
    }


    // 組み立て済みpdfを呼び出し元へ返却（saveは呼び出し元で実行）
    return pdf;
  } catch (error) {
    console.log(error);
    toast({
      title: "PDFの生成に失敗しました。",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
    return null;
  } finally {
    const overlay = document.getElementById('pdf-loading-overlay');
    if (overlay) overlay.remove();
  }
}

/**
 * 振り返りセクションのキャプチャ画像をPDFにスライス分割で追加する。
 * 単純なY軸スライスのみ実施。ページ幅は余白20mmを取り、縦は1ページに収まらない場合に
 * 次ページに「画像を負方向にオフセットして」貼り付けることで連続表示を実現する。
 *
 * @param pdf 追記対象のjsPDFインスタンス
 * @param imageDataUrl キャプチャ済み画像のDataURL
 * @param options.addPageBefore 描画前にaddPage()するか（セクション境界で利用）
 */
export function addReviewSectionWithSplit(
  pdf: jsPDF,
  imageDataUrl: string,
  options?: { addPageBefore?: boolean }
): void {
  if (options?.addPageBefore) {
    pdf.addPage();
  }

  const imgProps = pdf.getImageProperties(imageDataUrl);
  const pdfFullWidth = pdf.internal.pageSize.getWidth();
  const pdfFullHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const imgWidth = pdfFullWidth - margin * 2;
  const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

  // 1ページに収まる場合は1回で完了
  if (imgHeight <= pdfFullHeight - margin * 2) {
    pdf.addImage(imageDataUrl, 'JPEG', margin, margin, imgWidth, imgHeight);
    return;
  }

  // Y軸方向にスライス分割（次ページは画像を負方向にオフセットして連続表示）
  const pageContentHeight = pdfFullHeight - margin * 2;
  let position = margin;
  let heightLeft = imgHeight;

  pdf.addImage(imageDataUrl, 'JPEG', margin, position, imgWidth, imgHeight);
  heightLeft -= pageContentHeight;

  while (heightLeft > 0) {
    pdf.addPage();
    // 次ページに貼る画像のY座標は「margin - 既描画分の累積高さ」
    position = margin - (imgHeight - heightLeft);
    pdf.addImage(imageDataUrl, 'JPEG', margin, position, imgWidth, imgHeight);
    heightLeft -= pageContentHeight;
  }
}

/**
 * 振り返り3画面PDF用：詰め改ページ方式でブロックを追加する。
 *
 * 各ブロックを「現ページの現在Y位置」に追記し、入らなければ改ページしてから貼り付ける。
 * 1ページに収まりきらない大きなブロックは従来通りY軸スライス分割で複数ページに分かれる。
 * 呼び出し側は {@link ReviewFlowCursor} で現在の貼り付けY座標を保持し、ブロックごとに更新する。
 *
 * @param pdf 追記対象のjsPDFインスタンス
 * @param imageDataUrl キャプチャ済みブロック画像のDataURL
 * @param cursor 現ページの貼り付けカーソル（in/out）
 * @param options.minTopGap ブロック間の最小縦間隔(mm)。0なら詰める。デフォルト2mm。
 */
export interface ReviewFlowCursor {
  /** 次にブロックを貼り付けるY座標（mm） */
  y: number;
}

export function addReviewBlockFlow(
  pdf: jsPDF,
  imageDataUrl: string,
  cursor: ReviewFlowCursor,
  options?: { minTopGap?: number; pageBreakBefore?: boolean }
): void {
  const imgProps = pdf.getImageProperties(imageDataUrl);
  const pdfFullWidth = pdf.internal.pageSize.getWidth();
  const pdfFullHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const imgWidth = pdfFullWidth - margin * 2;
  const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
  const minGap = options?.minTopGap ?? 1;
  const pageBottom = pdfFullHeight - margin;

  // ⓪ ブロック前に強制改ページ（compareMode 切替やPartNo繰り返しの境界用）
  if (options?.pageBreakBefore) {
    pdf.addPage();
    cursor.y = margin;
  }

  // ① 現ページに入るか判定。入らなければ改ページしてカーソルを margin にリセット
  const tryY = cursor.y + (cursor.y > margin ? minGap : 0);
  const fitsInCurrentPage = tryY + imgHeight <= pageBottom;

  if (fitsInCurrentPage) {
    pdf.addImage(imageDataUrl, 'JPEG', margin, tryY, imgWidth, imgHeight);
    cursor.y = tryY + imgHeight;
    return;
  }

  // ② 改ページしてから貼り付け。ただし ⓪ で既に新ページに来ている場合（cursor.y === margin）は
  // 重ねて addPage しない（空ページが挟まるのを防ぐ）。
  const alreadyAtFreshPage = cursor.y === margin;
  if (!alreadyAtFreshPage) {
    pdf.addPage();
  }
  const pageContentHeight = pdfFullHeight - margin * 2;
  if (imgHeight <= pageContentHeight) {
    pdf.addImage(imageDataUrl, 'JPEG', margin, margin, imgWidth, imgHeight);
    cursor.y = margin + imgHeight;
    return;
  }

  // ③ ブロックが1ページを超える場合は従来同様Y軸スライス分割で連続表示
  let position = margin;
  let heightLeft = imgHeight;
  pdf.addImage(imageDataUrl, 'JPEG', margin, position, imgWidth, imgHeight);
  heightLeft -= pageContentHeight;
  while (heightLeft > 0) {
    pdf.addPage();
    position = margin - (imgHeight - heightLeft);
    pdf.addImage(imageDataUrl, 'JPEG', margin, position, imgWidth, imgHeight);
    heightLeft -= pageContentHeight;
  }
  // 最終ページ末尾までは描画済みのため、カーソルは「最終ページ上で残り画像が露出している下端」
  // = margin + (imgHeight % pageContentHeight) 相当だが、安全のため最終ページの貼り付け下端を計算
  const lastPageImageBottom = position + imgHeight; // position は margin から負方向にずらした最終値
  cursor.y = lastPageImageBottom;
}
